import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  Timestamp,
  deleteDoc,
  doc,
  where,
  type QueryDocumentSnapshot,
  type DocumentData,
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { normalizeCommentReactions, toggleCommentReaction } from '../../utils/commentReactions';
import { resolveReactionProjectLabel } from '../../utils/commentReactionEvents';
import { getProjectDisplayNameWithoutNumber, parseLegacyTitle } from '../../utils/projectName';
import CommentReactions from '../CommentReactions';
import {
  Image,
  FileText,
  BarChart3,
  Presentation,
  Paperclip,
  Trash2,
  Send,
  Plus,
  ChevronDown,
  ChevronRight,
  Mail,
  MailX,
  Pencil,
  Pin,
  PinOff,
  CornerDownRight,
  Reply as ReplyIcon,
  X,
  Check,
  Maximize2,
  LayoutGrid,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import CommentScheduleControls from './CommentScheduleControls';
import {
  cancelScheduledComment,
  createScheduledTaskComment,
  scheduledCommentThreadKey,
  subscribeOwnPendingScheduledComments,
  type ScheduledCommentRow,
} from '../../utils/scheduledComments';
import { auth, db, storage } from '../../utils/firebase';
import {
  TaskComment,
  TaskCommentEmailDelivery,
  CommentAttachment,
  PdfAnnotation,
  ProjectMeetingNote,
  LeadComm,
  CommentReactionsMap,
  ProjectFile,
  TaskCommentKind,
  TaskStatus,
  CRMUser,
  ProjectTask,
} from '../../types';
import {
  aggregateGroupComments,
  type GroupCommentContext,
  type GroupMemberTaskRow,
} from '../../utils/groupComments';
import { isProductionCommentKey } from '../../utils/productionCommentAccess';
import { buildProductionMentionCandidates } from '../../utils/productionMentionCandidates';
import { collectTaskAssignedSupplierOrgIdsForComments } from '../../utils/taskSupplierMentions';
import {
  architectDiscussionUnreadCount,
  filterArchitectDecisionComments,
  filterInternalComments,
  isArchitectDecisionAudience,
  isDesignGroupCommentKey,
  mentionUsersForCommentAudience,
  notifyUserIdsForCommentAudience,
  type CommentAudience,
} from '../../utils/commentAudience';
import { isArchitectRole } from '../../utils/userRoles';
import { architectDesignTaskCommentsQuery } from '../../utils/architectDesignCommentQuery';
import { getAssignedArchitectUids } from '../../utils/architectProjectAccess';
import ArchitectDecisionsTour, {
  clearArchitectDecisionsTourSeen,
  hasSeenArchitectDecisionsTour,
} from './ArchitectDecisionsTour';
import { ArchitectRoleBadge, ThreadVisibilityBadge } from './ArchitectRoleBadge';
import {
  markArchitectDiscussionAsRead,
  subscribeToReadReceipts,
  type ReadReceiptMap,
} from '../../utils/taskReadReceipts';
import './ArchitectRoleBadge.css';
import GroupCommentActionRequiredStrip from './GroupCommentActionRequiredStrip';
import CommentsOpenHelper from './CommentsOpenHelper';
import './CommentsOpenHelper.css';
import { pdfAnnotationHasUnreadForUser } from '../../utils/pdfAnnotationService';
import GroupPdfCommentsTab from './GroupPdfCommentsTab';
import GroupMeetingNotesTab from './GroupMeetingNotesTab';
import LeadCommsSection from '../leads/LeadCommsSection';
import { useAuth } from '../../utils/AuthContext';
import { useCRM } from '../../utils/CRMContext';
import { useProjectHubScopedData } from '../../hooks/useProjectHubProjectData';
import { useOptionalProjectUploadModal } from './ProjectUploadModalProvider';
import { togglePdfAnnotationReaction } from '../../utils/pdfAnnotationService';
import { isCsvType } from '../../utils/projectFileService';
import { formatDistanceToNow, format } from 'date-fns';
import { canDeleteTaskComment } from '../../utils/commentPermissions';
import { getOnboardingCommentQuerySpecs } from '../../utils/onboardingTaskBridge';
import {
  groupTaskCommentRepliesByThreadRoot,
  parentIdForTaskCommentReply,
} from '../../utils/taskCommentThread';
import { buildCommentAttachmentStorageFileName } from '../../utils/storageFileNames';
import { viewerDocumentIdForAttachment } from '../../utils/firestoreDocId';
import { ensureCrmStorageAccessBeforeUpload } from '../../utils/crmStorageAccess';
import { useCommentTyping } from '../../hooks/useCommentTyping';
import { useCommentThreadMeeting } from '../../hooks/useCommentThreadMeeting';
import { commentTypingThreadKey } from '../../utils/commentTypingService';
import CommentTypingIndicator from './CommentTypingIndicator';
import CommentThreadMeetingChip from './CommentThreadMeetingChip';
import RichCommentEditor, { RichCommentEditorHandle, RichCommentEditorPayload } from './RichCommentEditor';
import RichCommentRenderer from './RichCommentRenderer';
import QuestionAnswerSlot from './QuestionAnswerSlot';
import UntaggedSendHint, { useUntaggedSendGate } from './UntaggedSendHint';
import { mentionsNotifySomeone } from '../../utils/untaggedCommentConfirm';
import PlanReviewViewModal from '../pdf/PlanReviewViewModal';
import type { PlanReview } from '../../types';
import type { StructureRef } from '../../utils/proposalStructureRefs';
import './TaskComments.css';
import UserHoverCard from '../ui/UserHoverCard';
import {
  hasSeenCommentsOpenHelper,
  markCommentsOpenHelperSeen,
  type CommentsOpenHelperVariant,
} from '../../utils/commentsOpenHelper';
import { canAccessInspirationBoard, canAccessProjectHubChangeOrdersTab } from '../../utils/userRoles';
import { projectHubFilesFolderPath, projectHubViewPath } from '../../hooks/useProjectHubViewModeHotkeys';
import {
  HUB_HASH_FILES_FOLDER_TAGS,
  hubHashFolderPath,
  type HubHashTag,
} from '../../utils/commentHubHashMentions';
import {
  resolveInspirationBoardLink,
  shouldRenderLegacyBoardChip,
} from '../../utils/richComment';
import {
  commentHtmlHasTeamMention,
  collectTeamChannelUserIds,
  mentionsIncludeTeam,
  teamChannelMemberNames,
} from '../../utils/teamMention';
import {
  buildInspirationBoardPath,
  parseInspirationBoardLink,
} from '../../utils/notificationDeepLinks';

interface TaskCommentsProps {
  taskId: string;
  taskTitle?: string;
  projectId?: string;
  /** Hub deal id — passed into project file upload when attaching from comments. */
  dealId?: string;
  compact?: boolean;
  defaultExpanded?: boolean;
  /** Scroll to this taskComments document id (Slack / notifications deep link). */
  highlightCommentId?: string | null;
  onHighlightCommentConsumed?: () => void;
  /** Opens PlanReviewViewModal when the popout is shown (email / deep link). */
  initialPlanReviewId?: string | null;
  onInitialPlanReviewConsumed?: () => void;
  /**
   * When this token changes (and is non-null), the comments view auto-opens in
   * the popout modal. Use a fresh value (e.g. `Date.now()`) per request so the
   * effect re-fires even after the user manually closes the popout.
   */
  popoutRequestToken?: number | string | null;
  /** Fired when the fullscreen popout closes (backdrop, Esc, or close button). */
  onPopoutClose?: () => void;
  onOpenPdf?: (url: string, title: string, documentId: string, options?: { annotationId?: string; pageNumber?: number }) => void;
  onOpenImage?: (url: string, title: string, documentId: string, options?: { annotationId?: string; mimeType?: string }) => void;
  /** Optional CSV viewer (CsvViewerModal) — falls back to a plain anchor when omitted. */
  onOpenCsv?: (url: string, title: string, documentId: string, options?: { annotationId?: string; mimeType?: string }) => void;
  /** Internal team members for optional action-required picker in the composer. */
  actionRequiredOptions?: { uid: string; displayName: string }[];
  /** When the user picks someone and posts, update the task's action required field. */
  onSetActionRequired?: (userId: string) => void | Promise<void>;
  /** New comments are disabled when the task is completed. */
  taskStatus?: TaskStatus;
  /** Per-task composer: assignees used as the @team channel. */
  assignedTo?: string[] | null;
  /**
   * Group-consolidation mode. When provided, comments aggregate across the whole group
   * (all member tasks + group-keyed comments) instead of a single task, and a project-wide
   * PDF Comments tab is shown.
   */
  groupContext?: GroupCommentContext | null;
  /** All PDF annotations for the group's project (shown in the PDF Comments tab). */
  projectPdfAnnotations?: PdfAnnotation[];
  /** Fathom meeting notes for the group's project (PM-only Meeting Notes tab). */
  projectMeetingNotes?: ProjectMeetingNote[];
  /** Lead comms linked to this project (in-place stamp; shown alongside meeting notes). */
  leadComms?: LeadComm[];
  leadIdForComms?: string | null;
  /** When true, show the Meeting Notes tab (project_manager designation). */
  isProjectManager?: boolean;
  /** Group mode: member tasks for the Action Required strip above the composer. */
  groupMemberTasks?: GroupMemberTaskRow[];
  teamMembers?: Pick<CRMUser, 'uid' | 'displayName' | 'role' | 'company'>[];
  currentUserId?: string;
  onUpdateTaskForGroup?: (taskId: string, updates: Partial<ProjectTask>) => void | Promise<void>;
  onCompleteTaskForGroup?: (taskId: string) => void;
  /** Staff partner preview — hide the composer. */
  readOnly?: boolean;
  /** Architect portal / hub: Design decisions only. */
  architectSurface?: boolean;
  /** Deep link: open the matching Design tab (Comments, Architect Discussion, PDF). */
  initialTab?: GroupCommentsTab | null;
  /** Proposal structures for `/` mentions (this project / deal only). */
  structureMentionDealId?: string;
  structureMentions?: StructureRef[];
  structuresByDealId?: Map<string, StructureRef[]>;
}

export type GroupCommentsTab = 'comments' | 'architect' | 'pdf' | 'meetings';

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
};

/** Parse nested Firestore Timestamps (SDK Timestamp, plain seconds, or ISO). */
function firestoreValueToDate(value: unknown): Date {
  if (value == null) return new Date();
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? new Date() : value;
  }
  if (typeof value === 'object' && 'toDate' in value && typeof (value as { toDate: () => Date }).toDate === 'function') {
    try {
      const d = (value as { toDate: () => Date }).toDate();
      return Number.isNaN(d.getTime()) ? new Date() : d;
    } catch {
      return new Date();
    }
  }
  const o = value as { seconds?: number; nanoseconds?: number };
  if (typeof o.seconds === 'number') {
    const d = new Date(o.seconds * 1000 + Math.floor((o.nanoseconds || 0) / 1e6));
    return Number.isNaN(d.getTime()) ? new Date() : d;
  }
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? new Date() : d;
  }
  return new Date();
}

/** Same as firestoreValueToDate but returns null for null/undefined (for nullable timestamps like pinnedAt). */
function firestoreValueToDateOrNull(value: unknown): Date | null {
  if (value == null) return null;
  return firestoreValueToDate(value);
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

/** Prefer `emailDeliveries` from Cloud Function; fall back to legacy architect-only successes. */
function mergedEmailDeliveries(c: TaskComment): TaskCommentEmailDelivery[] {
  if (c.emailDeliveries && c.emailDeliveries.length > 0) return c.emailDeliveries;
  if (c.externalEmailDeliveries && c.externalEmailDeliveries.length > 0) {
    return c.externalEmailDeliveries.map(e => ({
      recipientUserId: e.userId,
      recipientEmail: '',
      recipientName: e.recipientName,
      recipientRole: e.role,
      eventType: e.eventType,
      status: 'sent' as const,
      emailSubject: e.emailSubject,
      sentAt: e.sentAt,
      triggeredByUserId: '',
      triggeredByName: '',
    }));
  }
  return [];
}

/** Renders mail badge + details in a portaled tooltip so `.tc-feed` overflow does not clip it. */
const TaskCommentEmailCluster: React.FC<{ emailRows: TaskCommentEmailDelivery[] }> = ({ emailRows }) => {
  const clusterRef = useRef<HTMLSpanElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const n = emailRows.length;
  const failed = emailRows.filter(r => r.status === 'failed').length;
  const sent = n - failed;
  const allFailed = failed === n;
  const anyFailed = failed > 0;

  const reposition = useCallback(() => {
    const anchor = clusterRef.current;
    const pop = popoverRef.current;
    if (!anchor || !pop) return;
    const r = anchor.getBoundingClientRect();
    const pw = Math.max(pop.offsetWidth, 1);
    const ph = Math.max(pop.offsetHeight, 1);
    const margin = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = r.left + r.width / 2 - pw / 2;
    left = Math.max(margin, Math.min(left, vw - pw - margin));
    let top = r.top - ph - margin;
    if (top < margin) {
      top = r.bottom + margin;
    }
    if (top + ph > vh - margin) {
      top = Math.max(margin, vh - ph - margin);
    }
    setPos((prev) => (prev && prev.top === top && prev.left === left ? prev : { top, left }));
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    reposition();
    const id = window.requestAnimationFrame(() => reposition());
    return () => window.cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- emailRows is a new array each parent render; n/failed track content
  }, [open, reposition, n, failed]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => reposition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, reposition]);

  const tooltipBody = (
    <>
      <span className="tc-email-delivery-tooltip-status">
        Email notifications
        {n > 1 ? ` · ${sent} sent${anyFailed ? ` · ${failed} failed` : ''}` : anyFailed ? ' · Failed' : ' · Sent'}
      </span>
      {emailRows.map((r, i) => (
        <div key={`${r.recipientUserId}-${i}-${Number.isFinite(r.sentAt?.getTime?.()) ? r.sentAt.getTime() : i}`} className="tc-email-cluster-recipient">
          <span className="tc-email-cluster-recipient-hdr">
            {r.status === 'sent' ? (
              <Mail size={11} strokeWidth={2.25} className="tc-email-cluster-recipient-ok" aria-hidden />
            ) : (
              <MailX size={11} strokeWidth={2.25} className="tc-email-cluster-recipient-bad" aria-hidden />
            )}
            <span className="tc-email-cluster-recipient-status">
              {r.status === 'sent' ? 'Sent' : 'Failed'}
            </span>
          </span>
          <span className="tc-email-delivery-tooltip-block">
            <span className="tc-email-delivery-tooltip-k">To</span>
            <span className="tc-email-delivery-tooltip-v">
              {r.recipientEmail || '—'}
              {r.recipientName ? ` (${r.recipientName})` : ''}
            </span>
          </span>
          <span className="tc-email-delivery-tooltip-block">
            <span className="tc-email-delivery-tooltip-k">Role</span>
            <span className="tc-email-delivery-tooltip-v">{r.recipientRole}</span>
          </span>
          <span className="tc-email-delivery-tooltip-block">
            <span className="tc-email-delivery-tooltip-k">Event</span>
            <span className="tc-email-delivery-tooltip-v">
              {r.eventType === 'mention' ? 'Mention' : 'Comment'}
            </span>
          </span>
          <span className="tc-email-delivery-tooltip-block">
            <span className="tc-email-delivery-tooltip-k">Triggered by</span>
            <span className="tc-email-delivery-tooltip-v">
              {r.triggeredByName || '—'}
              {r.triggeredByUserId ? ` · ${r.triggeredByUserId.slice(0, 8)}…` : ''}
            </span>
          </span>
          <span className="tc-email-delivery-tooltip-block">
            <span className="tc-email-delivery-tooltip-k">When</span>
            <span className="tc-email-delivery-tooltip-v">
              {Number.isFinite(r.sentAt?.getTime?.())
                ? format(r.sentAt, 'MMM d, yyyy · h:mm:ss a')
                : '—'}
            </span>
          </span>
          {r.emailSubject && (
            <span className="tc-email-delivery-tooltip-block">
              <span className="tc-email-delivery-tooltip-k">Subject</span>
              <span className="tc-email-delivery-tooltip-subj">{r.emailSubject}</span>
            </span>
          )}
          {r.status === 'failed' && r.error && (
            <span className="tc-email-delivery-tooltip-block tc-email-delivery-tooltip-error">
              <span className="tc-email-delivery-tooltip-k">Error</span>
              <span className="tc-email-delivery-tooltip-v">{r.error}</span>
            </span>
          )}
        </div>
      ))}
    </>
  );

  return (
    <>
      <span
        ref={clusterRef}
        className={`tc-email-cluster ${anyFailed ? 'tc-email-cluster--has-fail' : ''} ${allFailed ? 'tc-email-cluster--all-fail' : ''}`}
        tabIndex={0}
        aria-label={
          anyFailed
            ? `${sent} email(s) sent, ${failed} failed — hover for details`
            : `${n} email notification(s) sent — hover for details`
        }
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false);
        }}
      >
        {allFailed ? (
          <MailX size={12} strokeWidth={2.25} className="tc-email-cluster-icon" aria-hidden />
        ) : (
          <Mail size={12} strokeWidth={2.25} className="tc-email-cluster-icon" aria-hidden />
        )}
        {n > 1 && <span className="tc-email-cluster-count">{n}</span>}
      </span>
      {open &&
        createPortal(
          <div
            ref={popoverRef}
            className="tc-email-delivery-tooltip tc-email-delivery-tooltip--portal"
            style={
              pos
                ? { top: pos.top, left: pos.left }
                : { top: -9999, left: 0, visibility: 'hidden' as const }
            }
            role="tooltip"
          >
            {tooltipBody}
          </div>,
          document.body,
        )}
    </>
  );
};

function newStagedAttachKey(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function projectFileToCommentAttachment(pf: ProjectFile): CommentAttachment {
  return {
    id: pf.id || pf.url,
    name: pf.name,
    url: pf.url,
    type: pf.mimeType,
    size: pf.size,
  };
}

type StagedAttachment =
  | { key: string; kind: 'legacy'; file: File }
  | { key: string; kind: 'project'; attachment: CommentAttachment };

const getFileIcon = (type: string): React.ReactNode => {
  if (type.startsWith('image/')) return <Image size={14} />;
  if (type === 'application/pdf') return <FileText size={14} />;
  if (type.includes('word') || type.includes('document')) return <FileText size={14} />;
  if (type.includes('sheet') || type.includes('excel')) return <BarChart3 size={14} />;
  if (type.includes('presentation') || type.includes('powerpoint')) return <Presentation size={14} />;
  return <Paperclip size={14} />;
};

function mapTaskCommentDoc(d: QueryDocumentSnapshot<DocumentData>): TaskComment {
  const data = d.data();
  const externalEmailDeliveries = Array.isArray(data.externalEmailDeliveries)
    ? data.externalEmailDeliveries.map((row: Record<string, unknown>) => ({
        userId: String(row.userId ?? ''),
        recipientName: String(row.recipientName ?? ''),
        role: row.role as 'architect' | 'engineer',
        eventType: row.eventType as 'comment' | 'mention',
        emailSubject: String(row.emailSubject ?? ''),
        sentAt: (row.sentAt as { toDate?: () => Date })?.toDate?.() || new Date(),
      }))
    : undefined;
  const emailDeliveries = Array.isArray(data.emailDeliveries)
    ? data.emailDeliveries.map((row: Record<string, unknown>) => ({
        recipientUserId: String(row.recipientUserId ?? ''),
        recipientEmail: String(row.recipientEmail ?? ''),
        recipientName: String(row.recipientName ?? ''),
        recipientRole: String(row.recipientRole ?? ''),
        eventType: row.eventType as 'comment' | 'mention',
        status: row.status as 'sent' | 'failed',
        emailSubject: row.emailSubject != null ? String(row.emailSubject) : undefined,
        error: row.error != null ? String(row.error) : undefined,
        sentAt: firestoreValueToDate(row.sentAt),
        triggeredByUserId: String(row.triggeredByUserId ?? ''),
        triggeredByName: String(row.triggeredByName ?? ''),
      }))
    : undefined;
  return {
    id: d.id,
    ...data,
    reactions: normalizeCommentReactions(data.reactions),
    createdAt: data.createdAt?.toDate?.() || new Date(),
    updatedAt: data.updatedAt?.toDate?.(),
    editedAt: data.editedAt ? firestoreValueToDate(data.editedAt) : undefined,
    pinnedAt: firestoreValueToDateOrNull(data.pinnedAt),
    externalEmailDeliveries,
    emailDeliveries,
    inspirationBoard: parseInspirationBoardLink(data.inspirationBoard) || undefined,
  } as TaskComment;
}

function mapPdfAnnotationDoc(d: QueryDocumentSnapshot<DocumentData>): PdfAnnotation {
  const data = d.data();
  return {
    id: d.id,
    ...data,
    createdAt: data.createdAt?.toDate?.() || new Date(),
    replies: (data.replies || []).map((r: Record<string, unknown>) => ({
      ...r,
      createdAt: (r.createdAt as { toDate?: () => Date })?.toDate?.() || new Date(),
    })),
  } as PdfAnnotation;
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  Filter / kind constants                                                   */
/* ────────────────────────────────────────────────────────────────────────── */

type FilterId = 'all' | 'mentions' | 'mine' | 'attachments';

/** Chips excluding author scope — author filter is the pill select next to these chips. */
const SCOPE_FILTER_CHIPS: { id: Exclude<FilterId, 'mine'>; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'mentions', label: '@ Me' },
  { id: 'attachments', label: 'Has files' },
];

const KIND_OPTIONS: { id: TaskCommentKind; label: string; short: string }[] = [
  { id: 'note', label: 'Note', short: 'Note' },
  { id: 'decision', label: 'Decision', short: 'Decision' },
  { id: 'question', label: 'Question', short: 'Question' },
  { id: 'action', label: 'Action item', short: 'Action' },
];

const draftKey = (uid: string | undefined, taskId: string) => `tcDraft:${uid || 'anon'}:${taskId}`;

/** Plain-text excerpt of a comment for the "Replying to" chip + delete preview. */
function excerptOf(comment: TaskComment, max = 80): string {
  const src = comment.body || '';
  const oneLine = src.replace(/\s+/g, ' ').trim();
  return oneLine.length > max ? `${oneLine.slice(0, max)}…` : oneLine;
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  Main component                                                            */
/* ────────────────────────────────────────────────────────────────────────── */

const TaskComments: React.FC<TaskCommentsProps> = ({
  taskId,
  taskTitle,
  projectId,
  dealId,
  compact = false,
  defaultExpanded = false,
  highlightCommentId = null,
  onHighlightCommentConsumed,
  initialPlanReviewId = null,
  onInitialPlanReviewConsumed,
  popoutRequestToken = null,
  onPopoutClose,
  onOpenPdf,
  onOpenImage,
  onOpenCsv,
  actionRequiredOptions,
  onSetActionRequired,
  taskStatus,
  assignedTo,
  groupContext = null,
  projectPdfAnnotations,
  projectMeetingNotes,
  leadComms,
  leadIdForComms = null,
  isProjectManager = false,
  groupMemberTasks,
  teamMembers,
  currentUserId,
  onUpdateTaskForGroup,
  onCompleteTaskForGroup,
  readOnly = false,
  architectSurface = false,
  initialTab = null,
  structureMentionDealId,
  structureMentions,
  structuresByDealId,
}) => {
  const navigate = useNavigate();
  const isGroupMode = !!groupContext;
  const commentsLocked = readOnly || (!isGroupMode && taskStatus === 'completed');
  /** Stable scope id for draft autosave + attachment storage paths (task id, or a group key in group mode). */
  const scopeId =
    isGroupMode && groupContext
      ? `group-${groupContext.primaryProjectId}-${groupContext.primaryGroupId}`
      : taskId;

  const handleOpenPlanReviewDrawing = useCallback(
    (review: PlanReview) => {
      const pid = groupContext?.primaryProjectId || projectId;
      if (!pid || !review.documentId) return;
      const params = new URLSearchParams({ tab: 'files', file: review.documentId });
      if (review.streamId) params.set('track', review.streamId);
      window.location.assign(`/project-hub/${pid}?${params.toString()}`);
    },
    [groupContext?.primaryProjectId, projectId],
  );

  const [activeTab, setActiveTab] = useState<GroupCommentsTab>(
    initialTab === 'architect' || architectSurface ? 'architect' : 'comments',
  );
  const [readReceipts, setReadReceipts] = useState<ReadReceiptMap>({});
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [pdfAnnotations, setPdfAnnotations] = useState<PdfAnnotation[]>([]);
  const [draftHtml, setDraftHtml] = useState('');
  const untaggedSend = useUntaggedSendGate(draftHtml);
  const [initialDraftHtml, setInitialDraftHtml] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExpanded, setIsExpanded] = useState(compact ? defaultExpanded : true);
  const [stagedAttachments, setStagedAttachments] = useState<StagedAttachment[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileDragOver, setFileDragOver] = useState(false);
  const fileDragDepthRef = useRef(0);

  // New: composer extras
  const [replyTo, setReplyTo] = useState<TaskComment | null>(null);
  const [composerKind, setComposerKind] = useState<TaskCommentKind | null>(null);
  const [pendingScheduled, setPendingScheduled] = useState<ScheduledCommentRow[]>([]);
  const [composerActionRequiredUserId, setComposerActionRequiredUserId] = useState('');
  const [attachInspirationBoard, setAttachInspirationBoard] = useState(false);

  // New: per-comment edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingHtml, setEditingHtml] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // New: filter chips + "Mine" as author dropdown (self + thread authors)
  const [filter, setFilter] = useState<FilterId>('all');
  const [kindFilter, setKindFilter] = useState<TaskCommentKind | 'any'>('any');
  const [mineAuthorId, setMineAuthorId] = useState('');

  // Pop-out modal: lifts the same DOM subtree into a fullscreen overlay via
  // CSS so the editor (TipTap) and any in-progress draft, edit, reply, etc.
  // stay mounted across the toggle.
  const [isPopout, setIsPopout] = useState(popoutRequestToken != null);
  const [viewingPlanReviewId, setViewingPlanReviewId] = useState<string | null>(null);
  const [showCommentsOpenHelper, setShowCommentsOpenHelper] = useState(false);

  const uploadModal = useOptionalProjectUploadModal();

  const { user } = useAuth();
  const architectCommentUploads = architectSurface || isArchitectRole(user?.role);
  /** Meeting Notes / PDF tabs are staff-only; suppliers still get the comments feed. */
  const showStaffCommentTabs = user?.role !== 'supplier' && !architectSurface;
  const [showArchitectTour, setShowArchitectTour] = useState(false);
  const typingThreadKey = useMemo(() => {
    if (isGroupMode && groupContext) {
      return commentTypingThreadKey(
        'group',
        groupContext.primaryProjectId,
        groupContext.primaryGroupId,
      );
    }
    return commentTypingThreadKey('task', taskId);
  }, [groupContext, isGroupMode, taskId]);
  const typingLabel = useCommentTyping(
    typingThreadKey,
    draftHtml,
    !commentsLocked && user?.role !== 'supplier',
  );

  const quickMeetingEnabled =
    showStaffCommentTabs
    && !readOnly
    && !commentsLocked
    && activeTab === 'comments';

  const quickMeetingTitle = useMemo(() => {
    if (taskTitle?.trim()) return `Quick chat · ${taskTitle.trim()}`;
    if (isGroupMode) return 'Quick chat · Group comments';
    return 'Quick chat · Comments';
  }, [taskTitle, isGroupMode]);

  const threadMeeting = useCommentThreadMeeting({
    enabled: quickMeetingEnabled && !!typingThreadKey,
    threadKey: typingThreadKey,
    user: user ?? null,
    meetingTitle: quickMeetingTitle,
    projectId: projectId || groupContext?.primaryProjectId,
  });
  const {
    crmUsers,
    taskComments: crmTaskComments,
    enhancedDeals,
    timberProjects,
    rfqProjects,
    rfqSubmissions,
    productionCommentAccess,
    supplierOrgs,
  } = useCRM();
  // Inside the Project Hub a selected project provides scoped comments; outside
  // (portals, vendor portal) fall back to the org-wide/supplier-scoped CRM cache.
  const scopedHubData = useProjectHubScopedData();
  const allTaskComments = scopedHubData?.taskComments ?? crmTaskComments;
  const inspirationBoardTarget = useMemo(() => {
    let resolvedDealId = (dealId || '').trim();
    if (!resolvedDealId && projectId) {
      resolvedDealId = timberProjects.find((p) => p.id === projectId)?.dealId?.trim() || '';
    }
    if (!resolvedDealId && groupContext?.primaryProjectId) {
      resolvedDealId =
        timberProjects.find((p) => p.id === groupContext.primaryProjectId)?.dealId?.trim() || '';
    }
    if (!resolvedDealId) return null;
    const leadId = enhancedDeals.find((d) => d.id === resolvedDealId)?.leadId?.trim() || '';
    if (!leadId) return null;
    return { leadId, dealId: resolvedDealId };
  }, [dealId, enhancedDeals, groupContext?.primaryProjectId, projectId, timberProjects]);
  const canAttachInspirationBoard =
    !!inspirationBoardTarget && canAccessInspirationBoard(user?.role, user?.designations);
  const hubProjectId = (projectId || groupContext?.primaryProjectId || '').trim();
  const canAccessChangeOrdersTab = canAccessProjectHubChangeOrdersTab(
    user?.role,
    user?.designations,
  );
  const resolvedStructureDealId = (structureMentionDealId || dealId || '').trim();
  const resolvedStructureMentions = structureMentions ?? [];

  const hubHashMentionIds = useMemo((): HubHashTag[] => {
    const ids: HubHashTag[] = [];
    if (canAttachInspirationBoard) ids.push('board');
    if (hubProjectId) {
      if (!architectSurface) ids.push('tl');
      ids.push('files');
      if (canAccessChangeOrdersTab) ids.push('cos');
      // `#files-<folder>` — one per default project folder (plus Unsorted).
      ids.push(...HUB_HASH_FILES_FOLDER_TAGS);
    }
    return ids;
  }, [architectSurface, canAccessChangeOrdersTab, canAttachInspirationBoard, hubProjectId]);
  const handleHubHashClick = useCallback(
    (tag: HubHashTag) => {
      if (tag === 'board') {
        if (inspirationBoardTarget) {
          navigate(
            buildInspirationBoardPath(inspirationBoardTarget.leadId, inspirationBoardTarget.dealId),
          );
        }
        return;
      }
      if (!hubProjectId) return;
      const folderPath = hubHashFolderPath(tag);
      if (folderPath) {
        navigate(projectHubFilesFolderPath(hubProjectId, folderPath));
        return;
      }
      const mode = tag === 'tl' ? 'gantt' : tag === 'files' ? 'files' : 'changeOrders';
      navigate(projectHubViewPath(hubProjectId, mode));
    },
    [hubProjectId, inspirationBoardTarget, navigate],
  );
  const editorRef = useRef<RichCommentEditorHandle>(null);
  const editEditorRef = useRef<RichCommentEditorHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollFeedToBottom = useCallback(() => {
    if (activeTab !== 'comments' && activeTab !== 'architect') return;
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [activeTab]);

  const scheduleScrollFeedToBottom = useCallback(() => {
    scrollFeedToBottom();
    requestAnimationFrame(() => {
      scrollFeedToBottom();
      requestAnimationFrame(scrollFeedToBottom);
    });
  }, [scrollFeedToBottom]);

  /* ── Mention candidate list (passed to the editor) ───────────────────── */
  const supplierOrgNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const org of supplierOrgs) {
      if (org.id && org.name?.trim()) map.set(org.id, org.name.trim());
    }
    return map;
  }, [supplierOrgs]);

  const hubTasks = scopedHubData?.tasks ?? [];
  const hubTaskGroups = scopedHubData?.taskGroups ?? [];
  const hubOnboardings = scopedHubData?.projectOnboardings ?? [];

  const taskAssignedSupplierOrgIds = useMemo(
    () =>
      collectTaskAssignedSupplierOrgIdsForComments({
        isGroupMode,
        groupContext,
        taskId,
        projectTasks: hubTasks,
        taskGroups: hubTaskGroups,
        projectOnboardings: hubOnboardings,
      }),
    [groupContext, hubOnboardings, hubTaskGroups, hubTasks, isGroupMode, taskId],
  );

  const mentionableUsers = useMemo(
    () => {
      const dealKey = (dealId || groupContext?.groupKey.split('::')[0] || '').trim();
      const projectIds = Array.from(
        new Set(
          [
            projectId,
            groupContext?.primaryProjectId,
            ...(groupContext?.projectIds || []),
          ].filter((id): id is string => !!id),
        ),
      );
      const deal = dealKey
        ? enhancedDeals.find((d) => d.id === dealKey) || null
        : null;

      return buildProductionMentionCandidates({
        viewerRole: user?.role,
        crmUsers,
        dealId: dealKey || null,
        deal,
        projectIds,
        productionCommentAccess,
        rfqProjects,
        rfqSubmissions,
        timberProjects,
        supplierOrgs,
        taskAssignedSupplierOrgIds,
        taskComments: allTaskComments,
        pdfAnnotations: projectPdfAnnotations,
      });
    },
    [
      allTaskComments,
      crmUsers,
      dealId,
      enhancedDeals,
      groupContext,
      productionCommentAccess,
      projectId,
      projectPdfAnnotations,
      rfqProjects,
      rfqSubmissions,
      supplierOrgs,
      taskAssignedSupplierOrgIds,
      timberProjects,
      user?.role,
    ],
  );

  const isDesignThread = !!(isGroupMode && groupContext && isDesignGroupCommentKey(groupContext.groupKey));
  const designAudienceEnabled = isDesignThread && !architectSurface && user?.role !== 'supplier';
  const audienceDeal = useMemo(() => {
    const dealKey = (dealId || groupContext?.groupKey.split('::')[0] || '').trim();
    return dealKey ? enhancedDeals.find((d) => d.id === dealKey) || null : null;
  }, [dealId, enhancedDeals, groupContext?.groupKey]);
  const audienceProject = useMemo(() => {
    const pid = projectId || groupContext?.primaryProjectId;
    return pid ? timberProjects.find((p) => p.id === pid) || null : null;
  }, [groupContext?.primaryProjectId, projectId, timberProjects]);
  const showArchitectDiscussionTab = designAudienceEnabled && showStaffCommentTabs;
  const effectiveComposerAudience: CommentAudience =
    architectSurface || isArchitectRole(user?.role) || activeTab === 'architect'
      ? 'architect'
      : 'internal';
  const mentionableUsersForComposer = useMemo(
    () => mentionUsersForCommentAudience(
      mentionableUsers,
      effectiveComposerAudience,
      architectSurface || isArchitectRole(user?.role),
    ),
    [architectSurface, effectiveComposerAudience, mentionableUsers, user?.role],
  );

  const photoForUid = useCallback((uid: string) => crmUsers.find(x => x.uid === uid)?.photoURL, [crmUsers]);

  /* ── PDF Comments tab (group mode): project-wide annotations + unread count ── */
  const groupProjectAnnotations = useMemo(
    () => {
      const list = isGroupMode ? projectPdfAnnotations ?? [] : [];
      return architectSurface ? filterArchitectDecisionComments(list) : list;
    },
    [architectSurface, isGroupMode, projectPdfAnnotations],
  );
  const pdfTabUnreadCount = useMemo(() => {
    if (!isGroupMode || !user?.id) return 0;
    return groupProjectAnnotations.reduce(
      (n, ann) => (pdfAnnotationHasUnreadForUser(ann, user.id) ? n + 1 : n),
      0,
    );
  }, [isGroupMode, groupProjectAnnotations, user?.id]);

  /* ── Group mode: aggregate from in-memory CRM comments (no per-task listener) ── */
  useEffect(() => {
    if (!isGroupMode || !groupContext) return;

    // Production threads live on groupCommentKey. Use that query for real
    // suppliers and staff "view as employee" preview — the vendor portal does
    // not hydrate the org-wide taskComments list.
    if (architectSurface && isDesignGroupCommentKey(groupContext.groupKey)) {
      const key = groupContext.groupKey?.trim();
      const dealIdFromKey = key?.includes('::') ? key.split('::')[0]?.trim() : '';
      const projectIds = Array.from(new Set(groupContext.projectIds.filter(Boolean)));
      if (!key || !dealIdFromKey || !projectIds.length) {
        setComments([]);
        return;
      }
      const rowsByProject = new Map<string, TaskComment[]>();
      const mergeRows = () => {
        const byId = new Map<string, TaskComment>();
        Array.from(rowsByProject.values()).forEach((rows) => {
          for (const row of rows) {
            if (row.id) byId.set(row.id, row);
          }
        });
        const merged = Array.from(byId.values()).sort(
          (a, b) => a.createdAt.getTime() - b.createdAt.getTime(),
        );
        setComments(merged);
      };
      const mapRow = (d: { id: string; data: () => Record<string, unknown> }) => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          reactions: normalizeCommentReactions(data.reactions),
          createdAt:
            data.createdAt instanceof Date ? data.createdAt : firestoreValueToDate(data.createdAt),
          editedAt: data.editedAt ? firestoreValueToDate(data.editedAt) : undefined,
          updatedAt: data.updatedAt ? firestoreValueToDate(data.updatedAt) : undefined,
          pinnedAt: firestoreValueToDateOrNull(data.pinnedAt),
        } as TaskComment;
      };
      const unsubs = projectIds.map((pid) =>
        onSnapshot(
          architectDesignTaskCommentsQuery(pid, dealIdFromKey),
          (snapshot) => {
            rowsByProject.set(pid, snapshot.docs.map(mapRow));
            mergeRows();
          },
          (err) => {
            console.error('[TaskComments] architect design listen failed', pid, err);
            rowsByProject.set(pid, []);
            mergeRows();
          },
        ),
      );
      return () => unsubs.forEach((unsub) => unsub());
    }

    if (isProductionCommentKey(groupContext.groupKey)) {
      const key = groupContext.groupKey?.trim();
      if (!key) {
        setComments([]);
        return;
      }
      const unsub = onSnapshot(
        query(collection(db, 'taskComments'), where('groupCommentKey', '==', key)),
        (snapshot) => {
          const rows = snapshot.docs.map((d) => {
            const data = d.data();
            return {
              id: d.id,
              ...data,
              reactions: normalizeCommentReactions(data.reactions),
              createdAt:
                data.createdAt instanceof Date
                  ? data.createdAt
                  : firestoreValueToDate(data.createdAt),
              editedAt: data.editedAt ? firestoreValueToDate(data.editedAt) : undefined,
              updatedAt: data.updatedAt ? firestoreValueToDate(data.updatedAt) : undefined,
              pinnedAt: firestoreValueToDateOrNull(data.pinnedAt),
            } as TaskComment;
          });
          rows.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
          setComments(rows);
        },
        (err) => {
          console.error('[TaskComments] supplier groupCommentKey listen failed:', err);
          setComments([]);
        },
      );
      return () => unsub();
    }

    // Aggregate the (small) group subset first, then normalize Firestore timestamp /
    // reaction shapes the global CRM subscription leaves raw, so pins/edits render right.
    const aggregated = aggregateGroupComments(allTaskComments, groupContext).map((c) => ({
      ...c,
      reactions: normalizeCommentReactions(c.reactions),
      createdAt: c.createdAt instanceof Date ? c.createdAt : firestoreValueToDate(c.createdAt),
      editedAt: c.editedAt ? firestoreValueToDate(c.editedAt) : undefined,
      updatedAt: c.updatedAt ? firestoreValueToDate(c.updatedAt) : undefined,
      pinnedAt: firestoreValueToDateOrNull(c.pinnedAt),
    }));
    setComments(aggregated);
  }, [architectSurface, isGroupMode, groupContext, allTaskComments]);

  const visibleComments = useMemo(() => {
    if (architectSurface || activeTab === 'architect') return filterArchitectDecisionComments(comments);
    if (showArchitectDiscussionTab && activeTab === 'comments') return filterInternalComments(comments);
    return comments;
  }, [activeTab, architectSurface, comments, showArchitectDiscussionTab]);

  const teamChannelUserIds = useMemo(() => {
    const fromTasks = notifyUserIdsForCommentAudience(
      isGroupMode && groupContext ? groupContext.notifyUserIds : (assignedTo || []),
      effectiveComposerAudience,
      crmUsers,
      audienceDeal,
      audienceProject,
    ) || [];
    return collectTeamChannelUserIds({
      notifyUserIds: fromTasks,
      comments: visibleComments,
    });
  }, [
    assignedTo,
    audienceDeal,
    audienceProject,
    crmUsers,
    effectiveComposerAudience,
    groupContext,
    isGroupMode,
    visibleComments,
  ]);

  const teamChannelNames = useMemo(
    () => teamChannelMemberNames(teamChannelUserIds, crmUsers, user?.id),
    [crmUsers, teamChannelUserIds, user?.id],
  );

  const draftHasTeamMention = commentHtmlHasTeamMention(draftHtml);

  const architectTabUnreadCount = useMemo(() => {
    if (!showArchitectDiscussionTab || !groupContext) return 0;
    return architectDiscussionUnreadCount(comments, readReceipts, groupContext.groupKey, user?.id);
  }, [comments, groupContext, readReceipts, showArchitectDiscussionTab, user?.id]);

  useEffect(() => {
    if (!isPopout) return;
    if (architectSurface) return;
    if (!designAudienceEnabled) return;
    if (hasSeenArchitectDecisionsTour('staff')) return;
    const timer = window.setTimeout(() => setShowArchitectTour(true), 400);
    return () => window.clearTimeout(timer);
  }, [architectSurface, designAudienceEnabled, isPopout]);

  useEffect(() => {
    const replay = () => {
      if (architectSurface) return;
      if (!designAudienceEnabled) return;
      clearArchitectDecisionsTourSeen('staff');
      setShowArchitectTour(true);
    };
    window.addEventListener('lctf:replay-architect-decisions-tour', replay);
    return () => window.removeEventListener('lctf:replay-architect-decisions-tour', replay);
  }, [architectSurface, designAudienceEnabled]);

  useEffect(() => {
    if (!user?.id || !showArchitectDiscussionTab) return;
    return subscribeToReadReceipts(user.id, setReadReceipts);
  }, [showArchitectDiscussionTab, user?.id]);

  useEffect(() => {
    if (architectSurface) {
      setActiveTab('architect');
      return;
    }
    if (initialTab === 'architect' || initialTab === 'pdf' || initialTab === 'meetings' || initialTab === 'comments') {
      setActiveTab(initialTab);
      return;
    }
    setActiveTab('comments');
  }, [architectSurface, initialTab, popoutRequestToken]);

  useEffect(() => {
    if (!highlightCommentId || !showArchitectDiscussionTab) return;
    const hit = comments.find((c) => c.id === highlightCommentId);
    if (!hit) return;
    const wanted = isArchitectDecisionAudience(hit.audience) ? 'architect' : 'comments';
    if (activeTab !== wanted) setActiveTab(wanted);
  }, [activeTab, comments, highlightCommentId, showArchitectDiscussionTab]);

  useEffect(() => {
    if (!isPopout || !user?.id || !groupContext) return;
    if (activeTab === 'architect' || architectSurface) {
      void markArchitectDiscussionAsRead(user.id, groupContext.groupKey);
    }
  }, [activeTab, architectSurface, groupContext, isPopout, user?.id]);

  /* ── Live taskComments + pdfAnnotations subscriptions (task mode only) ── */
  useEffect(() => {
    if (isGroupMode) return;
    if (!taskId) return;
    const specs = getOnboardingCommentQuerySpecs(taskId, projectId);
    const chunkByKey = new Map<string, TaskComment[]>();

    const mergeAll = () => {
      const byId = new Map<string, TaskComment>();
      Array.from(chunkByKey.values()).forEach((rows) => {
        for (const c of rows) {
          if (c.id) byId.set(c.id, c);
        }
      });
      setComments(
        Array.from(byId.values()).sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())
      );
    };

    const unsubs = specs.map((spec) => {
      const key = spec.projectId != null ? `${spec.taskId}|||${spec.projectId}` : spec.taskId;
      const q =
        spec.projectId != null
          ? query(
              collection(db, 'taskComments'),
              where('taskId', '==', spec.taskId),
              where('projectId', '==', spec.projectId),
              orderBy('createdAt', 'asc')
            )
          : query(
              collection(db, 'taskComments'),
              where('taskId', '==', spec.taskId),
              orderBy('createdAt', 'asc')
            );
      return onSnapshot(
        q,
        (snap) => {
          chunkByKey.set(key, snap.docs.map(mapTaskCommentDoc));
          mergeAll();
        },
        (err) => {
          console.error('[TaskComments] Failed to listen to taskComments:', err);
        }
      );
    });

    return () => {
      unsubs.forEach((u) => u());
    };
  }, [isGroupMode, taskId, projectId]);

  useEffect(() => {
    if (isGroupMode) return;
    if (!taskId) return;
    const specs = getOnboardingCommentQuerySpecs(taskId, projectId);
    const chunkByKey = new Map<string, PdfAnnotation[]>();

    const mergeAll = () => {
      const byId = new Map<string, PdfAnnotation>();
      Array.from(chunkByKey.values()).forEach((rows) => {
        for (const a of rows) {
          if (a.id) byId.set(a.id, a);
        }
      });
      setPdfAnnotations(
        Array.from(byId.values()).sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())
      );
    };

    const unsubs = specs.map((spec) => {
      const key = spec.projectId != null ? `${spec.taskId}|||${spec.projectId}` : spec.taskId;
      const q =
        spec.projectId != null
          ? query(
              collection(db, 'pdfAnnotations'),
              where('taskId', '==', spec.taskId),
              where('projectId', '==', spec.projectId),
              orderBy('createdAt', 'asc')
            )
          : query(
              collection(db, 'pdfAnnotations'),
              where('taskId', '==', spec.taskId),
              orderBy('createdAt', 'asc')
            );
      return onSnapshot(q, (snap) => {
        chunkByKey.set(key, snap.docs.map(mapPdfAnnotationDoc));
        mergeAll();
      });
    });

    return () => {
      unsubs.forEach((u) => u());
    };
  }, [isGroupMode, taskId, projectId]);

  /* ── Per-scope draft autosave ────────────────────────────────────────── */
  useEffect(() => {
    if (!scopeId) return;
    try {
      const stored = window.localStorage.getItem(draftKey(user?.id, scopeId));
      setInitialDraftHtml(stored || '');
      setDraftHtml(stored || '');
    } catch {
      setInitialDraftHtml('');
      setDraftHtml('');
    }
    setReplyTo(null);
    setComposerKind(null);
    setAttachInspirationBoard(false);
    // We re-bind the draft only when switching tasks/users.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeId, user?.id]);

  useEffect(() => {
    if (!commentsLocked) return;
    setReplyTo(null);
    setComposerKind(null);
    setComposerActionRequiredUserId('');
    setAttachInspirationBoard(false);
    setEditingId(null);
    setEditingHtml('');
  }, [commentsLocked, taskId]);

  useEffect(() => {
    if (!scopeId) return;
    const handle = window.setTimeout(() => {
      try {
        const key = draftKey(user?.id, scopeId);
        if (draftHtml && draftHtml !== '<p></p>') {
          window.localStorage.setItem(key, draftHtml);
        } else {
          window.localStorage.removeItem(key);
        }
      } catch {
        /* ignore quota / privacy mode failures */
      }
    }, 400);
    return () => window.clearTimeout(handle);
  }, [draftHtml, scopeId, user?.id]);

  /* ── Pop-out modal: open when parent issues a fresh request token ─────── */
  useEffect(() => {
    if (popoutRequestToken == null) return;
    setIsPopout(true);
  }, [popoutRequestToken]);

  const onInitialPlanReviewConsumedRef = useRef(onInitialPlanReviewConsumed);
  onInitialPlanReviewConsumedRef.current = onInitialPlanReviewConsumed;

  useEffect(() => {
    const rid = initialPlanReviewId?.trim();
    if (!rid || popoutRequestToken == null) return;
    setViewingPlanReviewId(rid);
    onInitialPlanReviewConsumedRef.current?.();
  }, [popoutRequestToken, initialPlanReviewId]);

  const onPopoutCloseRef = useRef(onPopoutClose);
  onPopoutCloseRef.current = onPopoutClose;
  const wasPopoutRef = useRef(false);
  useEffect(() => {
    if (wasPopoutRef.current && !isPopout) {
      onPopoutCloseRef.current?.();
    }
    wasPopoutRef.current = isPopout;
  }, [isPopout]);

  /* ── Pop-out modal: ESC to close + lock background scroll ─────────────── */
  useEffect(() => {
    if (!isPopout) return;
    const onKeyDown = (e: KeyboardEvent) => {
      // Don't intercept ESC while a mention/link popover is open inside the editor —
      // those handle their own dismissal. We only close the popout when ESC isn't
      // already being consumed by something with stopPropagation.
      if (e.key === 'Escape' && !e.defaultPrevented) {
        setIsPopout(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Body-class signal so CSS can lift any ancestor stacking context (e.g.
    // .pm-detail-overlay sits at z-index 1000 and creates a stacking context
    // that would otherwise cap the modal below the AppHeader at z-index 10000).
    // This lets the modal stay rendered in-tree (preserving TipTap state)
    // while still painting above the header.
    document.body.classList.add('tc-popout-open');
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
      document.body.classList.remove('tc-popout-open');
    };
  }, [isPopout]);

  const commentsOpenHelperVariant = useMemo((): CommentsOpenHelperVariant => {
    if (!isGroupMode) return 'task';
    const hasOpenTasks = (groupMemberTasks ?? []).some((t) => t.status !== 'completed');
    return hasOpenTasks ? 'group-ar' : 'group-tabs';
  }, [isGroupMode, groupMemberTasks]);

  const dismissCommentsOpenHelper = useCallback(() => {
    markCommentsOpenHelperSeen();
    setShowCommentsOpenHelper(false);
  }, []);

  useEffect(() => {
    if (!isPopout) {
      setShowCommentsOpenHelper(false);
      return;
    }
    if (hasSeenCommentsOpenHelper() || highlightCommentId) return;
    const timer = window.setTimeout(() => setShowCommentsOpenHelper(true), 320);
    return () => window.clearTimeout(timer);
  }, [isPopout, popoutRequestToken, highlightCommentId]);

  /* ── Drag-n-drop attachments (direct to comment storage) ───────────────── */
  const addLegacyFilesFromList = useCallback((fileList: FileList | File[]) => {
    if (commentsLocked) return;
    const files = Array.from(fileList);
    const validFiles = files.filter(f => {
      if (f.size > 100 * 1024 * 1024) {
        alert(`${f.name} exceeds 100MB limit.`);
        return false;
      }
      return true;
    });
    if (validFiles.length === 0) return;
    setStagedAttachments(prev => [
      ...prev,
      ...validFiles.map(f => ({ key: newStagedAttachKey(), kind: 'legacy' as const, file: f })),
    ]);
  }, [commentsLocked]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (commentsLocked) return;
    if (e.target.files) addLegacyFilesFromList(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const hasFilesInTransfer = (dt: DataTransfer) =>
    Array.from(dt.types || []).includes('Files');

  const handleFileDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (commentsLocked || !hasFilesInTransfer(e.dataTransfer)) return;
    fileDragDepthRef.current += 1;
    if (fileDragDepthRef.current === 1) setFileDragOver(true);
  };

  const handleFileDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    fileDragDepthRef.current -= 1;
    if (fileDragDepthRef.current <= 0) {
      fileDragDepthRef.current = 0;
      setFileDragOver(false);
    }
  };

  const handleFileDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (hasFilesInTransfer(e.dataTransfer)) e.dataTransfer.dropEffect = 'copy';
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (commentsLocked) return;
    fileDragDepthRef.current = 0;
    setFileDragOver(false);
    const list = e.dataTransfer.files;
    if (!list?.length) return;
    addLegacyFilesFromList(list);
  };

  const removeStagedAttachment = (key: string) => {
    setStagedAttachments(prev => prev.filter(s => s.key !== key));
  };

  const uploadLegacyFiles = useCallback(
    async (files: File[]): Promise<CommentAttachment[]> => {
      await auth.authStateReady();
      const firebaseUser = auth.currentUser;
      if (!firebaseUser || firebaseUser.isAnonymous) {
        throw new Error(
          'Firebase sign-in required for file uploads. Use your company Google or email sign-in (anonymous sessions cannot upload to Storage).',
        );
      }

      await ensureCrmStorageAccessBeforeUpload();

      const uploaded: CommentAttachment[] = [];
      const n = files.length;
      for (let i = 0; i < n; i++) {
        const file = files[i];
        const fileId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const objectName = buildCommentAttachmentStorageFileName(fileId, file.name);
        const filePath = `task-comments/${scopeId}/${objectName}`;
        const storageRef = ref(storage, filePath);
        const contentType = file.type || 'application/octet-stream';
        const task = uploadBytesResumable(storageRef, file, { contentType });
        await new Promise<void>((resolve, reject) => {
          task.on(
            'state_changed',
            (snap) => {
              const fileProgress = snap.totalBytes > 0 ? snap.bytesTransferred / snap.totalBytes : 0;
              setUploadProgress(((i + fileProgress) / n) * 100);
            },
            reject,
            () => resolve(),
          );
        });
        const url = await getDownloadURL(task.snapshot.ref);
        uploaded.push({ id: fileId, name: file.name, url, type: file.type, size: file.size });
        setUploadProgress(((i + 1) / n) * 100);
      }
      return uploaded;
    },
    [scopeId],
  );


  const scheduleThreadKey = useMemo(() => {
    if (isGroupMode && groupContext?.groupKey) {
      return scheduledCommentThreadKey({
        target: 'taskComments',
        groupCommentKey: groupContext.groupKey,
      });
    }
    return scheduledCommentThreadKey({ target: 'taskComments', taskId });
  }, [groupContext?.groupKey, isGroupMode, taskId]);

  useEffect(() => {
    if (!user?.id || commentsLocked) {
      setPendingScheduled([]);
      return;
    }
    return subscribeOwnPendingScheduledComments({
      db,
      authorId: user.id,
      threadKey: scheduleThreadKey,
      onChange: setPendingScheduled,
    });
  }, [commentsLocked, scheduleThreadKey, user?.id]);

  const submitNewComment = useCallback(
    async (
      payload?: RichCommentEditorPayload,
      options?: { replyToOverride?: TaskComment | null; source?: 'composer' | 'answer' },
    ) => {
      const isAnswer = options?.source === 'answer';
      const editorPayload = payload ?? editorRef.current?.getPayload();
      if (!editorPayload) return;
      const legacyFiles = isAnswer
        ? []
        : stagedAttachments.filter((s): s is Extract<StagedAttachment, { kind: 'legacy' }> => s.kind === 'legacy');
      const projectReady = isAnswer
        ? []
        : stagedAttachments.filter((s): s is Extract<StagedAttachment, { kind: 'project' }> => s.kind === 'project');

      const boardLink = resolveInspirationBoardLink({
        attachToggle: !isAnswer && attachInspirationBoard,
        bodyHtml: editorPayload.bodyHtml,
        bodyPlain: editorPayload.bodyPlain,
        target: inspirationBoardTarget,
      });
      const hasAttachments = isAnswer ? false : stagedAttachments.length > 0;
      if ((editorPayload.isEmpty && !hasAttachments && !boardLink) || !user || isSubmitting || commentsLocked) return;

      if (legacyFiles.length > 0 && !auth.currentUser) {
        window.alert(
          'Your session is not ready to upload files. Please refresh the page and try again. If this keeps happening, sign out and sign back in.'
        );
        return;
      }

      setIsSubmitting(true);
      setUploadProgress(0);

      try {
        let uploadedAttachments: CommentAttachment[] = projectReady.map(s => s.attachment);
        if (legacyFiles.length > 0) {
          uploadedAttachments = [
            ...uploadedAttachments,
            ...(await uploadLegacyFiles(legacyFiles.map(x => x.file))),
          ];
        }

        const docPayload: Record<string, unknown> =
          isGroupMode && groupContext
            ? {
                // Group-consolidated comment: no single task; aggregated by groupCommentKey.
                taskId: '',
                groupCommentKey: groupContext.groupKey,
                projectId: groupContext.primaryProjectId,
                groupId: groupContext.primaryGroupId,
                ...((dealId || groupContext.groupKey.split('::')[0] || '').trim()
                  ? { dealId: (dealId || groupContext.groupKey.split('::')[0] || '').trim() }
                  : {}),
                notifyUserIds: groupContext.notifyUserIds.length > 0 ? groupContext.notifyUserIds : null,
                authorId: user.id,
                authorName: user.name || user.email,
                authorInitials: getInitials(user.name || user.email || ''),
                body: editorPayload.bodyPlain,
                bodyHtml: editorPayload.bodyHtml,
                bodyFormat: 'html',
                mentions: editorPayload.mentions.length > 0 ? editorPayload.mentions : null,
                attachments: uploadedAttachments.length > 0 ? uploadedAttachments : null,
                createdAt: Timestamp.now(),
              }
            : {
                taskId,
                ...(projectId ? { projectId } : {}),
                authorId: user.id,
                authorName: user.name || user.email,
                authorInitials: getInitials(user.name || user.email || ''),
                body: editorPayload.bodyPlain,
                bodyHtml: editorPayload.bodyHtml,
                bodyFormat: 'html',
                mentions: editorPayload.mentions.length > 0 ? editorPayload.mentions : null,
                attachments: uploadedAttachments.length > 0 ? uploadedAttachments : null,
                createdAt: Timestamp.now(),
              };
        const effectiveReply = options && 'replyToOverride' in options ? options.replyToOverride : replyTo;
        if (effectiveReply?.id) {
          docPayload.parentId = parentIdForTaskCommentReply(effectiveReply, comments);
        }
        if (!isAnswer && composerKind) docPayload.kind = composerKind;
        if (boardLink) docPayload.inspirationBoard = boardLink;
        const postedAudience: CommentAudience =
          architectSurface || isArchitectRole(user.role)
            ? 'architect'
            : effectiveReply && isArchitectDecisionAudience(effectiveReply.audience)
              ? 'architect'
              : isDesignThread
                ? effectiveComposerAudience
                : 'internal';
        if (isDesignThread || architectSurface) {
          docPayload.audience = postedAudience;
        }
        if (isGroupMode && groupContext) {
          const fromTasks = notifyUserIdsForCommentAudience(
            groupContext.notifyUserIds,
            postedAudience,
            crmUsers,
            audienceDeal,
            audienceProject,
          );
          docPayload.notifyUserIds = mentionsIncludeTeam(editorPayload.mentions)
            ? collectTeamChannelUserIds({
                notifyUserIds: fromTasks,
                comments: visibleComments,
              })
            : fromTasks;
        } else if (mentionsIncludeTeam(editorPayload.mentions)) {
          docPayload.notifyUserIds = collectTeamChannelUserIds({
            notifyUserIds: notifyUserIdsForCommentAudience(
              assignedTo,
              postedAudience,
              crmUsers,
              audienceDeal,
              audienceProject,
            ),
            comments: visibleComments,
          });
        }
        if (mentionsIncludeTeam(editorPayload.mentions)) {
          docPayload.teamMention = true;
        }

        await addDoc(collection(db, 'taskComments'), docPayload);

        if (!isAnswer && composerActionRequiredUserId && onSetActionRequired) {
          try {
            await onSetActionRequired(composerActionRequiredUserId);
          } catch (arError) {
            console.error('Error setting action required from comment:', arError);
            window.alert('Comment was posted, but action required could not be updated. Try setting it from the task details.');
          }
        }

        if (!isAnswer) {
          editorRef.current?.clear();
          setDraftHtml('');
          setInitialDraftHtml('');
          try {
            window.localStorage.removeItem(draftKey(user?.id, scopeId));
          } catch {
            /* ignore */
          }
          setStagedAttachments([]);
          setReplyTo(null);
          setComposerKind(null);
          setComposerActionRequiredUserId('');
          setAttachInspirationBoard(false);
          setUploadProgress(0);
        }
        requestAnimationFrame(() => scrollFeedToBottom());
      } catch (error: unknown) {
        console.error('Error adding task comment:', error);
        const code = typeof error === 'object' && error && 'code' in error ? String((error as { code: string }).code) : '';
        const message = error instanceof Error ? error.message : String(error);
        window.alert(
          legacyFiles.length > 0
            ? `Comment was not sent. File upload failed (${code || 'error'}): ${message}`
            : `Could not send comment (${code || 'error'}): ${message}`
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      commentsLocked,
      comments,
      attachInspirationBoard,
      architectSurface,
      audienceDeal,
      audienceProject,
      composerActionRequiredUserId,
      crmUsers,
      dealId,
      composerKind,
      effectiveComposerAudience,
      groupContext,
      inspirationBoardTarget,
      isDesignThread,
      isGroupMode,
      isSubmitting,
      onSetActionRequired,
      projectId,
      assignedTo,
      replyTo,
      scopeId,
      stagedAttachments,
      taskId,
      uploadLegacyFiles,
      user,
      visibleComments,
      scrollFeedToBottom,
    ],
  );


  const scheduleNewComment = useCallback(
    async (when: Date) => {
      const editorPayload = editorRef.current?.getPayload();
      if (!editorPayload || !user || isSubmitting || commentsLocked) return;
      const hasBody =
        !editorPayload.isEmpty ||
        stagedAttachments.length > 0 ||
        (attachInspirationBoard && inspirationBoardTarget);
      if (!hasBody) return;
      if (!untaggedSend.allow(mentionsNotifySomeone(editorPayload.mentions, user?.id), 'click')) return;

      // Reuse the same payload shape as immediate send (without uploading new files for v1).
      const legacyFiles = stagedAttachments.filter(
        (s): s is Extract<StagedAttachment, { kind: 'legacy' }> => s.kind === 'legacy',
      );
      if (legacyFiles.length > 0) {
        throw new Error('Remove attachments to schedule, or send now. Scheduled comments with new uploads are not supported yet.');
      }
      const projectReady = stagedAttachments.filter(
        (s): s is Extract<StagedAttachment, { kind: 'project' }> => s.kind === 'project',
      );
      const boardLink = resolveInspirationBoardLink({
        attachToggle: attachInspirationBoard,
        bodyHtml: editorPayload.bodyHtml,
        bodyPlain: editorPayload.bodyPlain,
        target: inspirationBoardTarget,
      });
      const uploadedAttachments = projectReady.map((s) => s.attachment);

      const docPayload: Record<string, unknown> =
        isGroupMode && groupContext
          ? {
              taskId: '',
              groupCommentKey: groupContext.groupKey,
              projectId: groupContext.primaryProjectId,
              groupId: groupContext.primaryGroupId,
              ...((dealId || groupContext.groupKey.split('::')[0] || '').trim()
                ? { dealId: (dealId || groupContext.groupKey.split('::')[0] || '').trim() }
                : {}),
              notifyUserIds: groupContext.notifyUserIds.length > 0 ? groupContext.notifyUserIds : null,
              authorId: user.id,
              authorName: user.name || user.email,
              authorInitials: getInitials(user.name || user.email || ''),
              body: editorPayload.bodyPlain,
              bodyHtml: editorPayload.bodyHtml,
              bodyFormat: 'html',
              mentions: editorPayload.mentions.length > 0 ? editorPayload.mentions : null,
              attachments: uploadedAttachments.length > 0 ? uploadedAttachments : null,
            }
          : {
              taskId,
              ...(projectId ? { projectId } : {}),
              authorId: user.id,
              authorName: user.name || user.email,
              authorInitials: getInitials(user.name || user.email || ''),
              body: editorPayload.bodyPlain,
              bodyHtml: editorPayload.bodyHtml,
              bodyFormat: 'html',
              mentions: editorPayload.mentions.length > 0 ? editorPayload.mentions : null,
              attachments: uploadedAttachments.length > 0 ? uploadedAttachments : null,
            };
      if (replyTo?.id) {
        docPayload.parentId = parentIdForTaskCommentReply(replyTo, comments);
      }
      if (composerKind) docPayload.kind = composerKind;
      if (boardLink) docPayload.inspirationBoard = boardLink;
      const postedAudience: CommentAudience =
        architectSurface || isArchitectRole(user.role)
          ? 'architect'
          : replyTo && isArchitectDecisionAudience(replyTo.audience)
            ? 'architect'
            : isDesignThread
              ? effectiveComposerAudience
              : 'internal';
      if (isDesignThread || architectSurface) {
        docPayload.audience = postedAudience;
      }
      if (isGroupMode && groupContext) {
        const fromTasks = notifyUserIdsForCommentAudience(
          groupContext.notifyUserIds,
          postedAudience,
          crmUsers,
          audienceDeal,
          audienceProject,
        );
        docPayload.notifyUserIds = mentionsIncludeTeam(editorPayload.mentions)
          ? collectTeamChannelUserIds({
              notifyUserIds: fromTasks,
              comments: visibleComments,
            })
          : fromTasks;
      } else if (mentionsIncludeTeam(editorPayload.mentions)) {
        docPayload.notifyUserIds = collectTeamChannelUserIds({
          notifyUserIds: notifyUserIdsForCommentAudience(
            assignedTo,
            postedAudience,
            crmUsers,
            audienceDeal,
            audienceProject,
          ),
          comments: visibleComments,
        });
      }
      if (mentionsIncludeTeam(editorPayload.mentions)) {
        docPayload.teamMention = true;
      }

      await createScheduledTaskComment({
        db,
        authorId: user.id,
        authorName: user.name || user.email || 'Someone',
        authorInitials: getInitials(user.name || user.email || ''),
        scheduledAt: when,
        commentPayload: docPayload,
        threadKey: scheduleThreadKey,
        projectId:
          (typeof docPayload.projectId === 'string' && docPayload.projectId) ||
          projectId ||
          null,
        bodyPreview: editorPayload.bodyPlain || '(attachment)',
      });

      editorRef.current?.clear();
      setDraftHtml('');
      setInitialDraftHtml('');
      try {
        window.localStorage.removeItem(draftKey(user?.id, scopeId));
      } catch {
        /* ignore */
      }
      setStagedAttachments([]);
      setReplyTo(null);
      setComposerKind(null);
      setAttachInspirationBoard(false);
    },
    [
      architectSurface,
      assignedTo,
      attachInspirationBoard,
      audienceDeal,
      audienceProject,
      comments,
      commentsLocked,
      composerKind,
      crmUsers,
      dealId,
      effectiveComposerAudience,
      groupContext,
      inspirationBoardTarget,
      isDesignThread,
      isGroupMode,
      isSubmitting,
      projectId,
      replyTo,
      scheduleThreadKey,
      scopeId,
      stagedAttachments,
      taskId,
      untaggedSend,
      user,
      visibleComments,
    ],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = editorRef.current?.getPayload();
    if (!payload || isSubmitting || commentsLocked) return;
    const hasBody = !payload.isEmpty || stagedAttachments.length > 0 || (attachInspirationBoard && inspirationBoardTarget);
    if (!hasBody) return;
    if (!untaggedSend.allow(mentionsNotifySomeone(payload.mentions, user?.id), 'click')) return;
    void submitNewComment(payload);
  };

  const handleEditorSubmitShortcut = useCallback(
    (payload: RichCommentEditorPayload) => {
      const hasBody = !payload.isEmpty || stagedAttachments.length > 0 || (attachInspirationBoard && inspirationBoardTarget);
      if (!hasBody) return;
      if (!untaggedSend.allow(mentionsNotifySomeone(payload.mentions, user?.id), 'keyboard')) return;
      void submitNewComment(payload);
    },
    [
      attachInspirationBoard,
      inspirationBoardTarget,
      stagedAttachments.length,
      submitNewComment,
      untaggedSend,
      user?.id,
    ],
  );

  const handleDelete = async (commentId: string) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await deleteDoc(doc(db, 'taskComments', commentId));
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  /* ── Edit own comment ─────────────────────────────────────────────────── */
  const startEdit = useCallback((c: TaskComment) => {
    if (commentsLocked || !c.id) return;
    setEditingId(c.id);
    const initial = c.bodyHtml && c.bodyHtml.length > 0
      ? c.bodyHtml
      : `<p>${(c.body || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br/>')}</p>`;
    setEditingHtml(initial);
  }, [commentsLocked]);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditingHtml('');
  }, []);

  const saveEdit = useCallback(
    async (commentId: string) => {
      if (commentsLocked) return;
      const payload = editEditorRef.current?.getPayload();
      if (!payload || payload.isEmpty || !user) return;
      setEditSaving(true);
      try {
        await updateDoc(doc(db, 'taskComments', commentId), {
          body: payload.bodyPlain,
          bodyHtml: payload.bodyHtml,
          bodyFormat: 'html',
          mentions: payload.mentions.length > 0 ? payload.mentions : null,
          teamMention: mentionsIncludeTeam(payload.mentions) ? true : null,
          editedAt: Timestamp.now(),
          editedById: user.id,
          updatedAt: Timestamp.now(),
        });
        cancelEdit();
      } catch (error) {
        console.error('Error editing comment:', error);
        window.alert('Could not save edit. Try again.');
      } finally {
        setEditSaving(false);
      }
    },
    [cancelEdit, commentsLocked, user],
  );

  /* ── Pin / unpin ──────────────────────────────────────────────────────── */
  const togglePin = useCallback(
    async (c: TaskComment) => {
      if (!c.id || !user) return;
      try {
        if (c.pinnedAt) {
          await updateDoc(doc(db, 'taskComments', c.id), {
            pinnedAt: null,
            pinnedById: null,
            updatedAt: Timestamp.now(),
          });
        } else {
          await updateDoc(doc(db, 'taskComments', c.id), {
            pinnedAt: Timestamp.now(),
            pinnedById: user.id,
            updatedAt: Timestamp.now(),
          });
        }
      } catch (error) {
        console.error('Error toggling pin:', error);
      }
    },
    [user],
  );

  const getUserDisplayName = useCallback(
    (id: string) => crmUsers.find((u) => u.uid === id)?.displayName || id.slice(0, 8),
    [crmUsers]
  );

  const toggleTaskCommentReaction = useCallback(
    async (comment: Pick<TaskComment, 'id' | 'authorId' | 'projectId' | 'dealId'>, emoji: string) => {
      if (!user?.id || !comment.id) return;
      try {
        await toggleCommentReaction(db, doc(db, 'taskComments', comment.id), emoji, user.id, {
          fallbackAuthorId: comment.authorId,
          projectLabel: resolveReactionProjectLabel({
            projectId: comment.projectId,
            dealId: comment.dealId,
            deals: enhancedDeals,
            projects: timberProjects,
          }),
        });
      } catch (error) {
        console.error('Error toggling reaction:', error);
        window.alert('Could not save reaction. Try again.');
      }
    },
    [enhancedDeals, timberProjects, user?.id],
  );

  const togglePdfFeedReaction = useCallback(
    async (annotationId: string, replyIndex: number | null, emoji: string) => {
      if (!user?.id || !annotationId) return;
      try {
        await togglePdfAnnotationReaction(annotationId, user.id, emoji, replyIndex);
      } catch (error) {
        console.error('Error toggling PDF reaction:', error);
        window.alert('Could not save reaction. Try again.');
      }
    },
    [user?.id]
  );

  /* ── PDF feed (unchanged) ─────────────────────────────────────────────── */
  const docUrlToName = useMemo(() => {
    const map = new Map<string, string>();
    visibleComments.forEach(c => {
      c.attachments?.filter(a => a.type === 'application/pdf').forEach(a => { map.set(a.url, a.name); });
    });
    return map;
  }, [visibleComments]);

  const pdfFeedItems = useMemo(() => {
    const items: Array<{
      type: 'pdf';
      userId: string;
      userName: string;
      content: string;
      createdAt: Date;
      pageNumber?: number;
      docName: string;
      docUrl: string;
      annotationId: string;
      documentId: string;
      replyIndex: number | null;
      reactions: CommentReactionsMap;
    }> = [];
    pdfAnnotations.forEach(ann => {
      const docName = docUrlToName.get(ann.documentUrl) || docUrlToName.get(ann.documentId) || 'PDF';
      const docUrl = ann.documentUrl || ann.documentId;
      items.push({
        type: 'pdf',
        userId: String(ann.userId ?? ''),
        userName: ann.userName,
        content: ann.content,
        createdAt: ann.createdAt,
        pageNumber: ann.pageNumber,
        docName,
        docUrl,
        annotationId: ann.id ?? '',
        documentId: ann.documentId || docUrl,
        replyIndex: null,
        reactions: normalizeCommentReactions(ann.reactions),
      });
      ann.replies?.forEach((r, replyIdx) => {
        items.push({
          type: 'pdf',
          userId: String(r.userId ?? ''),
          userName: r.userName,
          content: r.content,
          createdAt: r.createdAt instanceof Date ? r.createdAt : new Date((r.createdAt as unknown as string | number)),
          pageNumber: ann.pageNumber,
          docName,
          docUrl,
          annotationId: ann.id ?? '',
          documentId: ann.documentId || docUrl,
          replyIndex: replyIdx,
          reactions: normalizeCommentReactions(r.reactions),
        });
      });
    });
    return items.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
  }, [pdfAnnotations, docUrlToName]);

  const authorFilterOptions = useMemo(() => {
    const m = new Map<string, string>();
    visibleComments.forEach((c) => {
      const id = String(c.authorId ?? '').trim();
      if (!id) return;
      const name = (c.authorName || '').trim() || `${id.slice(0, 8)}…`;
      if (!m.has(id)) m.set(id, name);
    });
    return Array.from(m.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [visibleComments]);

  useEffect(() => {
    setMineAuthorId('');
    setFilter('all');
  }, [scopeId]);

  useEffect(() => {
    if (filter !== 'mine' || !mineAuthorId || mineAuthorId === user?.id) return;
    const ids = new Set(authorFilterOptions.map((o) => o.value));
    if (!ids.has(mineAuthorId)) setMineAuthorId('');
  }, [filter, mineAuthorId, authorFilterOptions, user?.id]);

  /* ── Filtering ────────────────────────────────────────────────────────── */

  const commentMatchesFilter = useCallback(
    (c: TaskComment): boolean => {
      if (kindFilter !== 'any' && c.kind !== kindFilter) return false;
      const mineUid = (mineAuthorId || user?.id || '').trim();
      let chipOk = false;
      switch (filter) {
        case 'mentions':
          chipOk = Array.isArray(c.mentions) && c.mentions.some((m) => m.userId === user?.id);
          break;
        case 'mine':
          chipOk = String(c.authorId ?? '') === String(mineUid);
          break;
        case 'attachments':
          chipOk = Array.isArray(c.attachments) && c.attachments.length > 0;
          break;
        case 'all':
        default:
          chipOk = true;
      }
      if (!chipOk) return false;
      return true;
    },
    [filter, kindFilter, mineAuthorId, user?.id],
  );

  /* ── Pinned + threaded layout ─────────────────────────────────────────── */

  type FeedItem = { type: 'comment'; data: TaskComment } | { type: 'pdf'; data: typeof pdfFeedItems[0] };

  /** Top-level (non-reply) comments in chronological order, filtered. */
  const repliesByThreadRoot = useMemo(
    () => groupTaskCommentRepliesByThreadRoot(visibleComments),
    [visibleComments],
  );

  const visibleTopLevelComments = useMemo(() => {
    return visibleComments
      .filter((c) => !c.parentId)
      .filter((c) => {
        if (commentMatchesFilter(c)) return true;
        const threadReplies = c.id ? repliesByThreadRoot.get(c.id) || [] : [];
        return threadReplies.some((reply) => commentMatchesFilter(reply));
      });
  }, [visibleComments, commentMatchesFilter, repliesByThreadRoot]);

  const visiblePinnedComments = useMemo(
    () => visibleComments.filter((c) => c.pinnedAt && commentMatchesFilter(c)),
    [visibleComments, commentMatchesFilter],
  );

  /** Chronological feed (top-level comments + their replies + pdfs), filtered. */
  const mergedFeed: FeedItem[] = useMemo(() => {
    const regular: FeedItem[] = visibleTopLevelComments.map(c => ({ type: 'comment' as const, data: c }));
    const pdfBaseVisible = (filter === 'all' || filter === 'mine') && kindFilter === 'any';
    const mineUid = (mineAuthorId || user?.id || '').trim();
    const pdfFiltered = pdfBaseVisible
      ? pdfFeedItems.filter((d) => {
          if (filter === 'all') return true;
          return String(d.userId ?? '') === String(mineUid);
        })
      : [];
    const pdf: FeedItem[] = pdfFiltered.map(d => ({ type: 'pdf' as const, data: d }));
    return [...regular, ...pdf].sort((a, b) => {
      const tA = a.type === 'comment' ? a.data.createdAt : a.data.createdAt;
      const tB = b.type === 'comment' ? b.data.createdAt : b.data.createdAt;
      return tA.getTime() - tB.getTime();
    });
  }, [visibleTopLevelComments, pdfFeedItems, filter, kindFilter, mineAuthorId, user?.id]);

  /* ── Deep-link highlight ──────────────────────────────────────────────── */
  useEffect(() => {
    if (!highlightCommentId || !onHighlightCommentConsumed) return;

    const hit = comments.find((c) => c.id === highlightCommentId);
    if (!hit) return;
    if (showArchitectDiscussionTab) {
      const wanted = isArchitectDecisionAudience(hit.audience) ? 'architect' : 'comments';
      if (activeTab !== wanted) {
        setActiveTab(wanted);
        return;
      }
    }

    let cancelled = false;
    let done = false;
    let giveUpId: number | undefined;
    let flashClearId: number | undefined;
    const maxAttempts = architectSurface ? 48 : 24;

    const finish = () => {
      if (done || cancelled) return;
      done = true;
      onHighlightCommentConsumed();
    };

    const tryScroll = (attempt: number) => {
      const el =
        document.getElementById(`hub-task-comment-${highlightCommentId}`) ||
        document.getElementById(`hub-task-comment-${highlightCommentId}-pinned`);
      if (cancelled || done) return;
      if (el) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        el.classList.remove('tc-item--flash');
        // Force reflow so re-adding the class restarts the animation reliably.
        void el.offsetWidth;
        el.classList.add('tc-item--flash');
        if (flashClearId !== undefined) window.clearTimeout(flashClearId);
        flashClearId = window.setTimeout(() => el.classList.remove('tc-item--flash'), 2800);
        if (giveUpId !== undefined) window.clearTimeout(giveUpId);
        // Keep highlight id until after the flash so layout effects don't
        // scroll the feed to bottom mid-animation.
        window.setTimeout(finish, 400);
        return;
      }
      if (attempt < maxAttempts) {
        window.setTimeout(() => tryScroll(attempt + 1), 100);
      } else {
        finish();
      }
    };

    giveUpId = window.setTimeout(finish, architectSurface ? 12000 : 8000);
    const start = window.setTimeout(() => tryScroll(0), architectSurface ? 120 : 40);

    return () => {
      cancelled = true;
      window.clearTimeout(start);
      if (giveUpId !== undefined) window.clearTimeout(giveUpId);
      if (flashClearId !== undefined) window.clearTimeout(flashClearId);
    };
  }, [
    highlightCommentId,
    comments,
    activeTab,
    showArchitectDiscussionTab,
    onHighlightCommentConsumed,
    architectSurface,
  ]);

  useLayoutEffect(() => {
    if (highlightCommentId) return;
    scrollFeedToBottom();
  }, [mergedFeed.length, activeTab, highlightCommentId, scrollFeedToBottom]);

  useLayoutEffect(() => {
    if (!isPopout || highlightCommentId) return;
    scheduleScrollFeedToBottom();
    const timers = [80, 200, 400].map((ms) => window.setTimeout(scrollFeedToBottom, ms));
    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [
    isPopout,
    popoutRequestToken,
    highlightCommentId,
    mergedFeed.length,
    scheduleScrollFeedToBottom,
    scrollFeedToBottom,
  ]);

  /* ── Render helpers ───────────────────────────────────────────────────── */

  const renderAttachments = (atts: CommentAttachment[]) => (
    <div className="tc-attachments">
      {atts.map(att => {
        const isPdf = att.type === 'application/pdf';
        const isImg = !!att.type?.startsWith('image/');
        const isCsv = isCsvType(att.type, att.name);
        if (isPdf && onOpenPdf) {
          return (
            <button key={att.id} type="button" className="tc-attachment-chip" title={`${att.name} (${formatFileSize(att.size)})`} onClick={() => onOpenPdf(att.url, att.name, viewerDocumentIdForAttachment(att))}>
              <span className="tc-attachment-icon">{getFileIcon(att.type)}</span>
              <span className="tc-attachment-name">{att.name}</span>
            </button>
          );
        }
        if (isImg && onOpenImage) {
          return (
            <button
              key={att.id}
              type="button"
              className="tc-attachment-chip"
              title={`${att.name} (${formatFileSize(att.size)})`}
              onClick={() => onOpenImage(att.url, att.name, viewerDocumentIdForAttachment(att), { mimeType: att.type })}
            >
              <span className="tc-attachment-icon">{getFileIcon(att.type)}</span>
              <span className="tc-attachment-name">{att.name}</span>
            </button>
          );
        }
        if (isCsv && onOpenCsv) {
          return (
            <button
              key={att.id}
              type="button"
              className="tc-attachment-chip"
              title={`${att.name} (${formatFileSize(att.size)})`}
              onClick={() => onOpenCsv(att.url, att.name, viewerDocumentIdForAttachment(att), { mimeType: att.type })}
            >
              <span className="tc-attachment-icon">{getFileIcon(att.type)}</span>
              <span className="tc-attachment-name">{att.name}</span>
            </button>
          );
        }
        return (
          <a key={att.id} href={att.url} target="_blank" rel="noopener noreferrer" className="tc-attachment-chip" title={`${att.name} (${formatFileSize(att.size)})`}>
            <span className="tc-attachment-icon">{getFileIcon(att.type)}</span>
            <span className="tc-attachment-name">{att.name}</span>
          </a>
        );
      })}
    </div>
  );

  const kindMeta = (k?: TaskCommentKind) => KIND_OPTIONS.find((o) => o.id === k);

  const renderCommentItem = (c: TaskComment, options: { indent?: boolean; isPinnedSection?: boolean; idx: number }) => {
    const { indent, isPinnedSection, idx } = options;
    const isOwn = String(c.authorId ?? '') === String(user?.id ?? '');
    const emailRows = mergedEmailDeliveries(c);
    const km = kindMeta(c.kind);
    const isEditing = editingId === c.id;
    const canModerate = !!user && canDeleteTaskComment(user, c);

    return (
      <div
        key={`${c.id}${isPinnedSection ? '__pinned' : ''}`}
        id={c.id ? `hub-task-comment-${c.id}${isPinnedSection ? '-pinned' : ''}` : undefined}
        className={`tc-item ${isOwn ? 'tc-item-own' : ''}${indent ? ' tc-item--reply' : ''}${c.pinnedAt && !isPinnedSection ? ' tc-item--pinned' : ''}${isArchitectRole(crmUsers.find((u) => u.uid === c.authorId)?.role) ? ' tc-item--architect' : ''}`}
        style={{ animationDelay: `${idx * 30}ms` }}
      >
        {indent && <span className="tc-thread-line" aria-hidden />}
        <UserHoverCard
          className={`tc-avatar${isArchitectRole(crmUsers.find((u) => u.uid === c.authorId)?.role) ? ' tc-avatar--architect' : ''}`}
          name={c.authorName}
          photoURL={photoForUid(c.authorId)}
          userId={c.authorId}
          crmUser={crmUsers.find((u) => u.uid === c.authorId)}
        />
        <div className="tc-item-body">
          <div className="tc-item-meta">
            <span className="tc-item-author">{c.authorName}</span>
            {isArchitectRole(crmUsers.find((u) => u.uid === c.authorId)?.role) && <ArchitectRoleBadge compact />}
            {c.source === 'slack' && <span className="tc-item-badge-slack">Slack</span>}
            {c.planReviewId && (
              <span className="tc-item-badge-plan-review" title="Completed plan review">
                Plan review
              </span>
            )}
            {c.inspirationBoard && (
              <span className="tc-item-badge-board" title="Includes an inspiration board link">
                Board
              </span>
            )}
            {km && (
              <span className={`tc-kind-badge tc-kind-badge--${km.id}`} title={`${km.label} comment`}>
                {km.short}
              </span>
            )}
            <span className="tc-item-time">{formatDistanceToNow(c.createdAt, { addSuffix: true })}</span>
            {c.editedAt && (
              <span className="tc-edited-pill" title={`Edited ${format(c.editedAt, 'MMM d, yyyy · h:mm a')}`}>
                edited
              </span>
            )}
            {emailRows.length > 0 && <span className="tc-item-meta-dot" aria-hidden />}
            {emailRows.length > 0 && <TaskCommentEmailCluster emailRows={emailRows} />}

            <div className="tc-item-actions">
              {!commentsLocked && !isPinnedSection && c.id && (
                <button
                  type="button"
                  className="tc-item-iconbtn"
                  onClick={() => setReplyTo(c)}
                  title="Reply"
                  aria-label="Reply"
                >
                  <ReplyIcon size={11} />
                </button>
              )}
              {canModerate && c.id && (
                <button
                  type="button"
                  className="tc-item-iconbtn"
                  onClick={() => togglePin(c)}
                  title={c.pinnedAt ? 'Unpin' : 'Pin to top'}
                  aria-label={c.pinnedAt ? 'Unpin' : 'Pin to top'}
                >
                  {c.pinnedAt ? <PinOff size={11} /> : <Pin size={11} />}
                </button>
              )}
              {isOwn && user?.role !== 'supplier' && c.id && !isEditing && !commentsLocked && !c.planReviewId && (
                <button
                  type="button"
                  className="tc-item-iconbtn"
                  onClick={() => startEdit(c)}
                  title="Edit"
                  aria-label="Edit"
                >
                  <Pencil size={11} />
                </button>
              )}
              {canModerate && c.id && (
                <button
                  type="button"
                  className="tc-item-iconbtn tc-item-iconbtn--danger"
                  onClick={() => handleDelete(c.id!)}
                  title={isOwn ? 'Delete your comment' : 'Delete comment (moderator)'}
                  aria-label="Delete"
                >
                  <Trash2 size={11} />
                </button>
              )}
            </div>
          </div>

          {isEditing && c.id ? (
            <div className="tc-edit-pane">
              <RichCommentEditor
                ref={editEditorRef}
                initialHtml={editingHtml}
                mentionableUsers={mentionableUsersForComposer}
                supplierOrgNameById={supplierOrgNameById}
                taskId={scopeId}
                onSubmit={() => saveEdit(c.id!)}
                placeholder="Edit your comment…"
                disabled={editSaving}
                enableTeamMention
                mentionPrioritizePartners={effectiveComposerAudience === 'architect'}
                assignedArchitectUids={getAssignedArchitectUids(audienceDeal, audienceProject)}
                hubHashMentionIds={hubHashMentionIds}
                structureMentionDealId={
                  resolvedStructureDealId && resolvedStructureMentions.length
                    ? resolvedStructureDealId
                    : undefined
                }
                structureMentions={
                  resolvedStructureMentions.length ? resolvedStructureMentions : undefined
                }
              />
              <div className="tc-edit-actions">
                <button type="button" className="tc-edit-cancel" onClick={cancelEdit} disabled={editSaving}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="tc-edit-save"
                  onClick={() => saveEdit(c.id!)}
                  disabled={editSaving}
                >
                  <Check size={12} /> {editSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          ) : c.planReviewId ? (
            <div className="tc-plan-review-card">
              <p className="tc-plan-review-card-text">{c.body}</p>
              <button
                type="button"
                className="tc-plan-review-open-btn"
                onClick={() => setViewingPlanReviewId(c.planReviewId!)}
              >
                View plan review
              </button>
            </div>
          ) : (
            <>
              {(c.bodyHtml || c.body) && (
                <RichCommentRenderer
                  className="tc-item-text"
                  bodyHtml={c.bodyHtml}
                  body={c.body}
                  structureMentionDealId={resolvedStructureDealId || undefined}
                  structureMentions={resolvedStructureMentions}
                  structuresByDealId={structuresByDealId}
                  onHubHashClick={
                    hubProjectId || inspirationBoardTarget || c.inspirationBoard
                      ? tag => {
                          if (tag === 'board' && c.inspirationBoard) {
                            navigate(
                              buildInspirationBoardPath(
                                c.inspirationBoard.leadId,
                                c.inspirationBoard.dealId,
                              ),
                            );
                            return;
                          }
                          handleHubHashClick(tag);
                        }
                      : undefined
                  }
                />
              )}
              {shouldRenderLegacyBoardChip(c) && c.inspirationBoard && (
                <button
                  type="button"
                  className="tc-board-card"
                  onClick={() =>
                    navigate(
                      buildInspirationBoardPath(c.inspirationBoard!.leadId, c.inspirationBoard!.dealId),
                    )
                  }
                >
                  <span className="tc-board-card-icon" aria-hidden>
                    <LayoutGrid size={14} strokeWidth={1.85} />
                  </span>
                  <span className="tc-board-card-title">Board</span>
                  <span className="tc-board-card-cta">Open</span>
                </button>
              )}
            </>
          )}

          {user && c.id && !isEditing && (
            <CommentReactions
              classPrefix="tc-"
              reactions={c.reactions || {}}
              currentUserId={user.id}
              onToggle={(emoji) => toggleTaskCommentReaction(c, emoji)}
              getUserDisplayName={getUserDisplayName}
              revealOnHover
            />
          )}
          {!isEditing && c.attachments && c.attachments.length > 0 && renderAttachments(c.attachments)}
        </div>
      </div>
    );
  };

  // Unified return: keep the body in the same React tree position regardless of
  // popout/compact state. CSS handles the visual lift to a fullscreen modal so
  // the TipTap editor never unmounts, preserving in-progress drafts and focus.
  const sectionClass = [
    'tc-section',
    compact ? 'tc-compact' : '',
    compact && isExpanded ? 'tc-expanded' : '',
    isPopout ? 'tc-section--popout' : '',
  ]
    .filter(Boolean)
    .join(' ');

  // In compact mode the chevron toggles inline expansion; popout always reveals
  // the body regardless of compact-collapse state.
  const showBody = !compact || isExpanded || isPopout;

  const popoutHeading = useMemo(() => {
    const section = architectSurface
      ? 'Design decisions'
      : activeTab === 'architect'
        ? 'Architect Discussion'
        : activeTab === 'pdf'
          ? 'PDF Comments'
          : activeTab === 'meetings'
            ? 'Meeting Notes'
            : 'Comments';
    const group = architectSurface ? '' : (taskTitle || '').trim();
    const resolvedProjectId = (projectId || groupContext?.primaryProjectId || '').trim();
    const project = resolvedProjectId
      ? timberProjects.find((item) => item.id === resolvedProjectId)
      : undefined;
    const resolvedDealId = (dealId || project?.dealId || '').trim();
    const deal = resolvedDealId
      ? enhancedDeals.find((item) => item.id === resolvedDealId)
      : undefined;
    const projectNumber = (deal?.projectNumber || '').trim();
    const numberDisplay = projectNumber
      ? (projectNumber.startsWith('#') ? projectNumber : `#${projectNumber}`)
      : '';
    const descriptor =
      (deal?.descriptor || '').trim()
      || parseLegacyTitle(deal?.title || '').descriptor
      || getProjectDisplayNameWithoutNumber(deal).trim()
      || (project?.name || '').trim();
    const projectLabel = [numberDisplay, descriptor].filter(Boolean).join(' ');
    const parts = [section];
    if (group && group.toLowerCase() !== section.toLowerCase()) parts.push(group);
    if (projectLabel) parts.push(projectLabel);
    return parts.join(' · ');
  }, [
    activeTab,
    architectSurface,
    dealId,
    enhancedDeals,
    groupContext?.primaryProjectId,
    projectId,
    taskTitle,
    timberProjects,
  ]);

  return (
    <>
      {isPopout && (
        <div
          className="tc-popout-backdrop"
          onClick={() => setIsPopout(false)}
          aria-hidden
        />
      )}
      <div className={sectionClass}>
        {compact && !isPopout && (
          <button className="tc-collapse-header" onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            <span className="tc-collapse-label">{architectSurface ? 'Design decisions' : 'Comments'}</span>
            <span className="tc-collapse-count">{visibleComments.length}</span>
          </button>
        )}

        {isPopout ? (
          <div className="tc-popout-header">
            <span className="tc-popout-title" title={popoutHeading}>
              {popoutHeading}
            </span>
            <span className="tc-popout-count">{visibleComments.length}</span>
            <button
              type="button"
              className="tc-popout-close"
              onClick={() => setIsPopout(false)}
              title="Close (Esc)"
              aria-label="Close popup"
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          showBody && (
            <button
              type="button"
              className="tc-popout-trigger"
              onClick={() => setIsPopout(true)}
              title="Open comments in a popup"
              aria-label="Open comments in a popup"
            >
              <Maximize2 size={11} />
            </button>
          )
        )}

        {isGroupMode && showBody && showStaffCommentTabs && (
          <div className="tc-tabs" role="tablist" aria-label="Comments tabs">
            <button
              type="button"
              role="tab"
              data-tour="comments-tab"
              aria-selected={activeTab === 'comments'}
              className={`tc-tab${activeTab === 'comments' ? ' tc-tab--active' : ''}`}
              onClick={() => setActiveTab('comments')}
            >
              Comments
            </button>
            {showArchitectDiscussionTab && (
              <button
                type="button"
                role="tab"
                data-tour="architect-discussion-tab"
                aria-selected={activeTab === 'architect'}
                className={`tc-tab${activeTab === 'architect' ? ' tc-tab--active' : ''}${architectTabUnreadCount > 0 ? ' tc-tab--unread' : ''}`}
                onClick={() => setActiveTab('architect')}
              >
                Architect Discussion
                {architectTabUnreadCount > 0 && (
                  <span className="tc-tab-badge">{architectTabUnreadCount}</span>
                )}
              </button>
            )}
            <button
              type="button"
              role="tab"
              data-tour="pdf-comments-tab"
              aria-selected={activeTab === 'pdf'}
              className={`tc-tab${activeTab === 'pdf' ? ' tc-tab--active' : ''}${pdfTabUnreadCount > 0 ? ' tc-tab--unread' : ''}`}
              onClick={() => setActiveTab('pdf')}
            >
              PDF Comments
              {pdfTabUnreadCount > 0 && <span className="tc-tab-badge">{pdfTabUnreadCount}</span>}
            </button>
            {isProjectManager && (
              <button
                type="button"
                role="tab"
                data-tour="meeting-notes-tab"
                aria-selected={activeTab === 'meetings'}
                className={`tc-tab${activeTab === 'meetings' ? ' tc-tab--active' : ''}`}
                onClick={() => setActiveTab('meetings')}
              >
                Meeting Notes
                <span className="tc-tab-pm-badge" title="Visible only to project managers">PM only</span>
              </button>
            )}
          </div>
        )}

        {showBody && showArchitectDiscussionTab && (activeTab === 'comments' || activeTab === 'architect') && (
          <div className="tc-audience-hint">
            <ThreadVisibilityBadge variant={activeTab === 'architect' ? 'architect' : 'internal'} />
          </div>
        )}

        {showBody && renderFullUI()}
      </div>
      {!architectSurface && (
        <ArchitectDecisionsTour
          script="staff"
          open={showArchitectTour}
          onClose={() => setShowArchitectTour(false)}
        />
      )}
      {viewingPlanReviewId && (
        <PlanReviewViewModal
          reviewId={viewingPlanReviewId}
          onClose={() => setViewingPlanReviewId(null)}
          onOpenDrawing={handleOpenPlanReviewDrawing}
        />
      )}
    </>
  );

  function renderFullUI() {
    if (isGroupMode && activeTab === 'meetings' && isProjectManager) {
      return (
        <div className="tc-drop-zone tc-drop-zone--meetingtab">
          <GroupMeetingNotesTab notes={projectMeetingNotes ?? []} />
          {(leadIdForComms || (leadComms && leadComms.length > 0)) && (
            <div className="tc-lead-comms-under-meetings" style={{ marginTop: 16 }}>
              <LeadCommsSection
                leadId={leadIdForComms || ''}
                items={leadComms}
                title="Lead Comms"
                defaultExpanded
                readOnly={false}
              />
            </div>
          )}
        </div>
      );
    }

    if (isGroupMode && activeTab === 'pdf') {
      return (
        <div className="tc-drop-zone tc-drop-zone--pdftab">
          <GroupPdfCommentsTab
            annotations={groupProjectAnnotations}
            userId={user?.id || ''}
            crmUsers={crmUsers}
            onOpenPdf={onOpenPdf}
            onOpenImage={onOpenImage}
            onOpenCsv={onOpenCsv}
          />
        </div>
      );
    }

    const sendDisabled =
      isSubmitting ||
      uploadModal.uploading ||
      ((!draftHtml || draftHtml === '<p></p>') &&
        stagedAttachments.length === 0 &&
        !(attachInspirationBoard && inspirationBoardTarget));

    return (
      <div
        className={`tc-drop-zone ${fileDragOver ? 'tc-drop-zone--active' : ''}`}
        onDragEnter={handleFileDragEnter}
        onDragLeave={handleFileDragLeave}
        onDragOver={handleFileDragOver}
        onDrop={handleFileDrop}
      >
        {fileDragOver && (
          <div className="tc-drop-overlay" aria-hidden>
            <Paperclip size={28} strokeWidth={1.75} />
            <span>Drop files to attach</span>
          </div>
        )}

        {/* Filter chips */}
        {visibleComments.length > 0 && (
          <div className="tc-filter-chips" aria-label="Filter comments">
            <div className="tc-filter-chip-buttons" role="tablist">
              {SCOPE_FILTER_CHIPS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  role="tab"
                  aria-selected={filter === f.id}
                  className={`tc-filter-chip${filter === f.id ? ' tc-filter-chip--active' : ''}`}
                  onClick={() => setFilter(f.id)}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <select
              className={`tc-filter-chip tc-filter-mine-select${filter === 'mine' ? ' tc-filter-chip--active' : ''}`}
              aria-label="Filter by comment author"
              value={filter === 'mine' ? (mineAuthorId || user?.id || '') : ''}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) {
                  setFilter('all');
                  return;
                }
                setMineAuthorId(v);
                setFilter('mine');
              }}
            >
              <option value="">Author</option>
              {user?.id ? (
                <option value={user.id}>Mine</option>
              ) : null}
              {authorFilterOptions
                .filter((o) => o.value !== user?.id)
                .map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
            </select>
            <div className="tc-filter-trailing">
              <select
                className="tc-filter-kind"
                value={kindFilter}
                onChange={(e) => setKindFilter(e.target.value as TaskCommentKind | 'any')}
                aria-label="Filter by kind"
              >
                <option value="any">All kinds</option>
                {KIND_OPTIONS.map((k) => (
                  <option key={k.id} value={k.id}>{k.label}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div className="tc-feed" ref={scrollRef}>
          {/* Pinned section */}
          {visiblePinnedComments.length > 0 && (
            <div className="tc-pinned-section" aria-label="Pinned comments">
              <div className="tc-pinned-header">
                <Pin size={11} aria-hidden />
                <span>Pinned</span>
              </div>
              {visiblePinnedComments.map((c, i) => renderCommentItem(c, { isPinnedSection: true, idx: i }))}
            </div>
          )}

          {mergedFeed.length === 0 && visiblePinnedComments.length === 0 && (
            <div className="tc-empty">
              {visibleComments.length === 0
                ? architectSurface
                  ? 'No discussion yet. LCTF will post here when there is something for you to review.'
                  : activeTab === 'architect'
                    ? 'Loop them in here when you need their call.'
                    : 'No comments yet. Start the conversation.'
                : 'No comments match the current filter.'}
            </div>
          )}

          {mergedFeed.map((item, idx) => {
            if (item.type === 'pdf') {
              const d = item.data;
              return (
                <div key={`pdf-${d.annotationId}-${d.replyIndex ?? 'root'}-${idx}`} className="tc-item tc-item-pdf" style={{ animationDelay: `${idx * 30}ms` }}>
                  <div className="tc-avatar tc-avatar-pdf">
                    <FileText size={12} />
                  </div>
                  <div className="tc-item-body">
                    <div className="tc-item-meta">
                      <span className="tc-item-author">{d.userName}</span>
                      <span className="tc-item-badge-pdf">PDF p.{d.pageNumber}</span>
                      <span className="tc-item-time">{formatDistanceToNow(d.createdAt, { addSuffix: true })}</span>
                    </div>
                    <div className="tc-item-text">{d.content}</div>
                    {user && d.annotationId && (
                      <div
                        className="tc-pdf-reactions"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        role="presentation"
                      >
                        <CommentReactions
                          classPrefix="tc-"
                          reactions={d.reactions || {}}
                          currentUserId={user.id}
                          onToggle={(emoji) => togglePdfFeedReaction(d.annotationId, d.replyIndex, emoji)}
                          getUserDisplayName={getUserDisplayName}
                          revealOnHover
                        />
                      </div>
                    )}
                    {onOpenPdf && (
                      <button type="button" className="tc-pdf-link" onClick={() => onOpenPdf(d.docUrl, d.docName, d.documentId, { annotationId: d.annotationId, pageNumber: d.pageNumber })}>
                        Open {d.docName}
                      </button>
                    )}
                  </div>
                </div>
              );
            }

            const c = item.data as TaskComment;
            const replies = c.id ? repliesByThreadRoot.get(c.id) || [] : [];
            const visibleReplies = replies.filter((r) => commentMatchesFilter(r));
            return (
              <React.Fragment key={c.id}>
                {renderCommentItem(c, { idx })}
                {visibleReplies.map((r, ri) => renderCommentItem(r, { indent: true, idx: idx + ri + 1 }))}
                {c.kind === 'question' && !commentsLocked && c.id ? (
                  <QuestionAnswerSlot
                    questionId={c.id}
                    hasReplies={visibleReplies.length > 0}
                    mentionableUsers={mentionableUsersForComposer}
                    supplierOrgNameById={supplierOrgNameById}
                    taskId={scopeId}
                    disabled={isSubmitting}
                    enableTeamMention
                    mentionPrioritizePartners={effectiveComposerAudience === 'architect'}
                    assignedArchitectUids={getAssignedArchitectUids(audienceDeal, audienceProject)}
                    onSubmit={async payload => {
                      await submitNewComment(payload, { replyToOverride: c, source: 'answer' });
                    }}
                    confirmWhenUntagged
                  />
                ) : null}
              </React.Fragment>
            );
          })}
        </div>

        {showCommentsOpenHelper && isPopout && activeTab === 'comments' && (
          <CommentsOpenHelper
            variant={commentsOpenHelperVariant}
            onDismiss={dismissCommentsOpenHelper}
          />
        )}

        {isGroupMode && !architectSurface && activeTab !== 'architect' && groupMemberTasks && groupMemberTasks.length > 0 && teamMembers && currentUserId && onUpdateTaskForGroup && (
          <GroupCommentActionRequiredStrip
            tasks={groupMemberTasks}
            teamMembers={teamMembers}
            currentUserId={currentUserId}
            onUpdateTask={onUpdateTaskForGroup}
            onCompleteTask={onCompleteTaskForGroup}
          />
        )}

        {/* Composer */}
        {commentsLocked ? (
          <div className="tc-composer-locked" role="status">
            {readOnly
              ? 'Preview is read-only.'
              : 'Comments are closed on completed tasks. Change the task status to add a comment.'}
          </div>
        ) : (
        <>
        {pendingScheduled.length > 0 && (
          <div className="tc-scheduled-list" aria-label="Your scheduled comments">
            {pendingScheduled.map((row) => (
              <div key={row.id} className="tc-scheduled-item">
                <div className="tc-scheduled-copy">
                  <strong>Scheduled for {row.scheduledAt.toLocaleString()}</strong>
                  <span>{row.bodyPreview || '(no text)'}</span>
                </div>
                <button
                  type="button"
                  className="tc-scheduled-cancel"
                  onClick={() => void cancelScheduledComment(db, row.id)}
                >
                  Cancel
                </button>
              </div>
            ))}
          </div>
        )}
        <form className="tc-composer" onSubmit={handleSubmit}>
          <CommentTypingIndicator label={typingLabel} />
          {/* Reply chip */}
          {replyTo && (
            <div className="tc-reply-chip">
              <CornerDownRight size={12} />
              <span className="tc-reply-chip-label">Replying to <strong>{replyTo.authorName}</strong>:</span>
              <span className="tc-reply-chip-excerpt">{excerptOf(replyTo)}</span>
              <button
                type="button"
                className="tc-reply-chip-clear"
                onClick={() => setReplyTo(null)}
                title="Cancel reply"
                aria-label="Cancel reply"
              >
                <X size={12} />
              </button>
            </div>
          )}
          {attachInspirationBoard && inspirationBoardTarget && (
            <div className="tc-board-chip">
              <LayoutGrid size={12} />
              <span className="tc-board-chip-label">Inspiration board</span>
              <span className="tc-board-chip-excerpt">Designers can open this project’s board from the comment</span>
              <button
                type="button"
                className="tc-reply-chip-clear"
                onClick={() => setAttachInspirationBoard(false)}
                title="Remove inspiration board card"
                aria-label="Remove inspiration board card"
              >
                <X size={12} />
              </button>
            </div>
          )}

          {stagedAttachments.length > 0 && (
            <div className="tc-composer-attachments">
              {stagedAttachments.map(s => {
                const name = s.kind === 'legacy' ? s.file.name : s.attachment.name;
                const mime = s.kind === 'legacy' ? s.file.type : s.attachment.type;
                return (
                  <div key={s.key} className="tc-staged-file">
                    <span className="tc-staged-icon">{getFileIcon(mime)}</span>
                    <span className="tc-staged-name">{name}</span>
                    <button type="button" className="tc-staged-remove" onClick={() => removeStagedAttachment(s.key)}>×</button>
                  </div>
                );
              })}
            </div>
          )}
          {(uploadModal.uploading || (uploadProgress > 0 && uploadProgress < 100)) && (
            <div className="tc-upload-bar">
              <div
                className="tc-upload-fill"
                style={{ width: `${uploadModal.uploading ? uploadModal.uploadProgress : uploadProgress}%` }}
              />
            </div>
          )}

          <RichCommentEditor
            ref={editorRef}
            initialHtml={initialDraftHtml}
            mentionableUsers={mentionableUsersForComposer}
            supplierOrgNameById={supplierOrgNameById}
            taskId={scopeId}
            onChange={setDraftHtml}
            onSubmit={handleEditorSubmitShortcut}
            placeholder={taskTitle ? `Write a comment on “${taskTitle}”…` : 'Write a comment… (@ to mention, ⌘/Ctrl-Enter to send)'}
            disabled={isSubmitting || uploadModal.uploading}
            enableTeamMention
            mentionPrioritizePartners={effectiveComposerAudience === 'architect'}
            assignedArchitectUids={getAssignedArchitectUids(audienceDeal, audienceProject)}
            hubHashMentionIds={hubHashMentionIds}
            structureMentionDealId={
              resolvedStructureDealId && resolvedStructureMentions.length
                ? resolvedStructureDealId
                : undefined
            }
            structureMentions={
              resolvedStructureMentions.length ? resolvedStructureMentions : undefined
            }
          />
          {draftHasTeamMention && (
            <div className="tc-team-mention-badge" role="status">
              <div className="tc-team-mention-badge-title">
                You’re notifying everybody with <strong>@team</strong>
              </div>
              <div className="tc-team-mention-badge-names">
                {teamChannelNames.length > 0
                  ? teamChannelNames.join(', ')
                  : 'No one else is in this channel'}
              </div>
            </div>
          )}

          {quickMeetingEnabled && threadMeeting.active ? (
            <CommentThreadMeetingChip
              layout="composerBanner"
              active={threadMeeting.active}
              meetUrl={threadMeeting.meeting?.meetUrl ?? null}
              hostName={threadMeeting.meeting?.hostName ?? ''}
              isHost={threadMeeting.isHost}
              loading={threadMeeting.loading}
              error={threadMeeting.error}
              calendarConnected={threadMeeting.calendarConnected}
              dialInPin={threadMeeting.meeting?.dialInPin}
              onStart={threadMeeting.startMeeting}
              onEnd={threadMeeting.endMeeting}
            />
          ) : null}

          <div className="tc-composer-row">
            <button
              type="button"
              className={`tc-question-toggle${composerKind === 'question' ? ' tc-question-toggle--on' : ''}`}
              onClick={() => setComposerKind(k => (k === 'question' ? null : 'question'))}
              aria-pressed={composerKind === 'question'}
              title="Ask a question — anyone in this thread can answer underneath"
            >
              Question
            </button>
            <select
              className="tc-kind-select"
              value={composerKind ?? ''}
              onChange={(e) => setComposerKind((e.target.value || null) as TaskCommentKind | null)}
              aria-label="Comment kind"
              title="Tag this comment"
            >
              <option value="">No tag</option>
              {KIND_OPTIONS.map((k) => (
                <option key={k.id} value={k.id}>{k.label}</option>
              ))}
            </select>

            {actionRequiredOptions && actionRequiredOptions.length > 0 && onSetActionRequired && (
              <select
                className="tc-kind-select tc-action-required-select"
                value={composerActionRequiredUserId}
                onChange={(e) => setComposerActionRequiredUserId(e.target.value)}
                aria-label="Action required user"
                title="Optionally set who needs to act on this task"
                disabled={isSubmitting || uploadModal.uploading}
              >
                <option value="">No action required</option>
                {actionRequiredOptions.map((u) => (
                  <option key={u.uid} value={u.uid}>{u.displayName}</option>
                ))}
              </select>
            )}

            <div className="tc-composer-spacer" />

            {quickMeetingEnabled && (
              <CommentThreadMeetingChip
                layout="composerIcon"
                active={threadMeeting.active}
                meetUrl={threadMeeting.meeting?.meetUrl ?? null}
                hostName={threadMeeting.meeting?.hostName ?? ''}
                isHost={threadMeeting.isHost}
                loading={threadMeeting.loading}
                error={threadMeeting.error}
                calendarConnected={threadMeeting.calendarConnected}
                dialInPin={threadMeeting.meeting?.dialInPin}
                onStart={threadMeeting.startMeeting}
                onEnd={threadMeeting.endMeeting}
                disabled={isSubmitting || uploadModal.uploading}
              />
            )}
            {canAttachInspirationBoard && (
              <button
                type="button"
                className={`tc-btn-attach${attachInspirationBoard ? ' tc-btn-attach--on' : ''}`}
                onClick={() => setAttachInspirationBoard((on) => !on)}
                title={attachInspirationBoard ? 'Remove inspiration board card' : 'Add inspiration board card'}
                aria-label={attachInspirationBoard ? 'Remove inspiration board card' : 'Add inspiration board card'}
                aria-pressed={attachInspirationBoard}
                disabled={isSubmitting || uploadModal.uploading}
              >
                <LayoutGrid size={14} />
              </button>
            )}
            <input
              type="file"
              ref={fileInputRef}
              className="tc-file-input"
              multiple
              accept={architectCommentUploads ? undefined : 'image/*,application/pdf,.pdf'}
              onChange={handleFileSelect}
            />
            <button
              type="button"
              className="tc-btn-attach"
              onClick={() => fileInputRef.current?.click()}
              title={architectCommentUploads ? 'Attach any file' : 'Attach files or screenshots'}
              disabled={isSubmitting || uploadModal.uploading}
            >
              <Plus size={14} />
            </button>
            <CommentScheduleControls
              disabled={sendDisabled}
              onSchedule={(when) => scheduleNewComment(when)}
            />
            <UntaggedSendHint open={untaggedSend.open} onDismiss={untaggedSend.dismiss}>
            <button
              type="submit"
              className="tc-btn-send"
              title={isSubmitting ? 'Sending…' : 'Send (⌘/Ctrl-Enter)'}
              disabled={sendDisabled}
            >
              <Send size={14} />
            </button>
            </UntaggedSendHint>
          </div>
        </form>
        </>
        )}
      </div>
    );
  }
};

export default TaskComments;
